/**
 * Boucle de jeu — assemblage de l'économie, du rendu, de l'audio et de l'interface.
 *
 * Ce fichier ne calcule rien et ne dessine rien : il **branche**. Toute la logique vit
 * dans `core`, tout le rendu dans `render`, toute l'interface dans `ui`. Si une règle de
 * jeu apparaît ici, c'est qu'elle est au mauvais endroit.
 *
 * Le tick logique tourne à 10 Hz et le rendu à 60 fps, **découplés** (§6) : la production
 * se calcule sur le temps écoulé, jamais sur le nombre d'images.
 */

import { JUICE } from '@/config/juice';
import { CLEARING, ECONOMY, GEM_SPENDING, PRESTIGE, REWARDED_AD } from '@/config/balance';
import { DAY_LENGTH_MS } from '@/core/dayCycle';
import { BUILDINGS } from '@/config/buildings';
import type { SoundEngine } from '@/audio/sound';
import {
  activeIsland,
  buildingCounts,
  canPlace,
  NEW_BUILDING_LEVEL,
  footprintCells,
  footprintOf,
  covers,
  moveBuilding,
  purchaseLand,
  recordVisitors,
  removeRoad,
  rotateBuilding,
  boostMultiplier,
  buyNightPass,
  buyQuestReroll,
  buySkip,
  buyStreakRepair,
  creditReturn,
  grantBoost,
  recoverReturn,
  buyLandWithGems,
  buyRush,
  prestige,
  prestigeProgress,
  sellBuilding,
  sellValue,
  questCounters,
  tidyRoads,
  upgradeBuilding,
  claimMilestones,
  createGameStore,
  placeBuilding,
  tick,
} from '@/core/gameState';
import type { CitySnapshot, PlacementResult } from '@/core/gameState';
import { defaultCityNames, duration, getLocale, n, t } from '@/i18n';
import type { TranslationKey } from '@/i18n';
import type { CityRenderer } from '@/render/cityRenderer';
import {
  claimQuest,
  claimStreak,
  claimWeekly,
  dayNumber,
  questProgress,
  rollDay,
  rollWeek,
  weekNumber,
  weeklyProgress,
} from '@/core/quests';
import { createPresence } from '@/net/presence';
import { clearSnapshot, createSaveScheduler, loadSnapshot } from '@/net/storage';
import { sealEnvelope, serialize } from '@/core/save';
import { createSync } from '@/net/sync';
import { splitElapsed } from '@/core/session';
import { EVENTS } from '@/analytics/events';
import { startAnalytics, track } from '@/analytics/posthog';
import { FEATURES } from '@/config/features';
import { LANDMARKS, landmarkByType } from '@/config/landmarks';
import { showRewardedAd } from '@/ads/rewarded';
import { buyDecoration, placeDecoration } from '@/core/decorations';
import { DECORATIONS, decorationById } from '@/config/decorations';
import { reconcilePaidGems } from '@/core/paidGems';
import { buyMaxLevel } from '@/core/maxLevel';
import { cleanupOrphanRoads, orphanRoadCount } from '@/core/roadCleanup';
import { nextAdvice } from '@/core/advisor';
import type { Advice, AdviceKind } from '@/core/advisor';
import { LIGHT_PERIOD_MS } from '@/core/traffic';
import { quoteTopUp, topUpCoins } from '@/core/topUp';
import { fetchPaidGemsTotal } from '@/net/gemGrants';
import { recoverPendingCheckouts, startCheckout, verifyCheckout } from '@/net/payments';
import { fetchNewsletterConsent, recordNewsletterConsent } from '@/net/newsletter';
import { currentAuthSession, ensureAuthSession, isSupabaseConfigured } from '@/net/supabase';
import { currentProduction } from '@/core/gameState';
import {
  maxAffordable,
  nextCost,
  nextLandCost,
  unlockedTypes,
  unlockProgress,
} from '@/core/economy';
import { prestigePreview } from '@/core/prestige';
import { activeBoost, visitsReceived } from '@/viral/visit';
import { createPrestigePanel } from '@/ui/prestigePanel';
import { createPromoSection } from '@/ui/promoSection';
import { createTracePanel } from '@/ui/tracePanel';
import { createTutorial } from '@/ui/tutorial';
import {
  boughtDecorationSellValue,
  moveVisitorTrace,
  relocateBuriedTraces,
  sellVisitorTrace,
  spawnVisitorTraces,
  traceSellValue,
} from '@/core/visitorTraces';
import { createPreferences } from '@/ui/preferences';
import { createToast } from '@/ui/components/overlays';
import { createConfirm } from '@/ui/confirm';
import { createBuildingPanel } from '@/ui/buildingPanel';
import { createQuestPanel } from '@/ui/questPanel';
import { createStorePanel } from '@/ui/storePanel';
import { createCitySection } from '@/ui/citySection';
import { clearCell, clearCost, regrowCell } from '@/core/clearing';
import {
  SOCIAL_CHANNELS,
  SOCIAL_REWARD_GEMS,
  hasClaimedSocial,
  remainingSocialGems,
  socialClaimId,
} from '@/core/socialRewards';
import {
  RENAME_CLAIM_ID,
  RENAME_REWARD_GEMS,
  isDefaultCityName,
  shouldNudgeRename,
} from '@/core/cityNaming';
import { skyAt } from '@/core/dayCycle';

/**
 * Où l'on retient la dernière relance de nom.
 *
 * Dans le stockage LOCAL, pas dans le snapshot : c'est une préférence d'affichage propre
 * à cet appareil, pas un fait de partie. La ranger dans la sauvegarde la ferait voyager
 * d'un téléphone à l'autre, et un joueur qui vient de voir le rappel sur son ordinateur
 * ne le reverrait pas sur son mobile — où le champ est pourtant plus facile à rater.
 */
const RENAME_NUDGE_KEY = 'villopia:rename-nudge';
import { createLeagueCard } from '@/ui/leagueCard';
import { createLudopiaSection } from '@/ui/ludopiaSection';
import { createRankingPanel } from '@/ui/rankingPanel';
import { createMilestoneBook } from '@/ui/milestoneBook';
import { createSocialSection } from '@/ui/socialSection';
import { createHud } from '@/ui/hud';
import { createInstallBanner, createInstallRow } from '@/ui/install';
import { createCompanion } from '@/ui/companion';
import { createTabbedSection } from '@/ui/tabbedSection';
import { createEraNotice } from '@/ui/eraNotice';
import { createUpdateNotice, watchForUpdates } from '@/ui/updateNotice';
import { createSettings } from '@/ui/settings';
import { createShell } from '@/ui/shell';
import { createShop } from '@/ui/shop';
import { createVersionInfo } from '@/ui/versionInfo';
import { createAccountSection } from '@/ui/account';
import { createAccountInvite } from '@/ui/accountInvite';
import { markPrompted, shouldPromptAccount } from '@/core/accountPrompt';
import type { AccountMoment, AccountPromptState } from '@/core/accountPrompt';
import { createReturnScreen } from '@/ui/returnScreen';

/**
 * Absence minimale pour mériter d'être ANNONCÉE, en millisecondes.
 *
 * Une minute : en dessous, le joueur sait très bien qu'il n'est allé nulle part, et le
 * message se lit comme un défaut d'affichage. Au-dessus, il a fait autre chose et le
 * retour vaut d'être salué.
 *
 * Distinct du seuil de l'écran de retour (`RETURN_SCREEN_MIN_MS`, dix minutes) : une
 * modale interrompt, un toast passe. Les deux n'ont pas à se déclencher au même moment.
 */
const ABSENCE_WORTH_TELLING_MS = 60_000;

export interface GameOptions {
  readonly renderer: CityRenderer;
  readonly canvas: HTMLCanvasElement;
  readonly sound: SoundEngine;
  readonly resourcesHost: HTMLElement;
  readonly shopHost: HTMLElement;
}

export interface Game {
  start(): void;
  stop(): void;
  getSnapshot(): CitySnapshot;
}

export function createGame(options: GameOptions): Game {
  const { renderer, canvas, sound, resourcesHost, shopHost } = options;

  // Reprise de la partie précédente (étape 8). `loadSnapshot` rend toujours un snapshot
  // valide : sauvegarde absente, illisible ou trop récente, on repart d'une ville neuve
  // plutôt que de propager un état douteux dans tout le moteur.
  const loaded = loadSnapshot(Date.now());
  const store = createGameStore(loaded.snapshot);
  /*
    L'échec de sauvegarde se DIT (audit P0.2). Navigation privée Safari, quota plein,
    stockage désactivé par une politique : une heure de jeu disparaissait à la
    fermeture, sans le moindre signal. Le toast part une seule fois par incident — et
    l'ordonnanceur, lui, garde l'état en attente pour réessayer.
  */
  const saves = createSaveScheduler({
    onWriteFailure: () => {
      toast.show({ title: t('common.saveFailed'), text: t('common.saveFailedHint') });
    },
  });

  /* ── HUD de ressources ─────────────────────────────────────────────── */

  // Le bandeau haut complet (D§3) : marque, ressources, pastilles, jalon, ligue,
  // visiteurs, réglages. Il existe en entier dès le premier chargement.
  const settings = createSettings();

  // Version + popup des nouveautés : puce en pied d'écran sur desktop (la zone est
  // libre), ligne dans Réglages partout — sur mobile le bas appartient au dock (D§3).
  const versionInfo = createVersionInfo();

  // Journal de la ville au retour (étape 8, VISION 4.2).
  /*
    B.6 — la pluie de pièces du retour, à la FERMETURE de l'écran : pendant qu'il est
    ouvert, le joueur lit son journal ; la célébration part quand il encaisse.
  */
  let pendingRain = 0;
  /*
    Durée de l'absence AFFICHÉE, retenue le temps de l'écran de retour.

    Elle ne peut pas être recalculée au moment du rachat : `updatedAt` a déjà été réécrit
    par le crédit, et `Date.now() - updatedAt` vaudrait alors quelques secondes. C'est une
    valeur de session, remise à zéro dès que l'écran se ferme — un rachat proposé pour une
    absence d'hier serait un bug de caisse.
  */
  let pendingAwayMs = 0;
  const returnScreen = createReturnScreen(
    () => {
      pendingAwayMs = 0;
      if (pendingRain > 0) {
        renderer.rainCoins(pendingRain, performance.now());
        pendingRain = 0;
      }
    },
    {
      onRecover: () => {
        if (pendingAwayMs <= 0) return false;

        const result = recoverReturn(store.getSnapshot(), pendingAwayMs, Date.now());
        if (!result.ok) {
          sound.play('refused');
          hud.refuse();
          if (result.refusal === 'too-few-gems') {
            toast.show({ title: t('gems.tooFew'), text: t('gems.tooFewHint') });
          }
          return false;
        }

        store.setSnapshot(result.snapshot);
        sound.play('milestone');
        // Le total de l'écran est RÉÉCRIT : payer en regardant l'ancien chiffre ne
        // ressemble pas à un achat, ça ressemble à une panne.
        returnScreen.recredit(pendingRain + result.coins);
        pendingRain += result.coins;
        // Une seule fois : l'offre porte sur CE retour, elle n'est pas un robinet.
        pendingAwayMs = 0;
        return true;
      },
    },
  );

  // Compte (§17) : attacher un e-mail à l'identité anonyme, sans jamais l'exiger.
  const account = createAccountSection();

  /*
    ══ TROIS MOMENTS POUR PROPOSER UN COMPTE (ROADMAP 6.2) ═══════════════════

    Jamais un mur : le §1 promet un jeu jouable en moins de cinq secondes sans compte, et
    le §3 fait de l'authentification une option. Trois instants seulement, choisis parce
    que le joueur vient d'y gagner quelque chose qu'il a une raison de vouloir garder.

    ⚠️ **L'horloge des rappels vit en localStorage, pas dans le snapshot.** Ce n'est pas
    de l'état de jeu : c'est un compteur de politesse propre à cet appareil. Le mettre
    dans la sauvegarde le ferait voyager jusqu'au serveur et revenir sur un autre
    navigateur — où l'invitation n'a jamais été vue.
  */
  const invite = createAccountInvite();
  const PROMPT_KEY = 'villopia:account:prompted';

  function readPromptState(): AccountPromptState {
    let lastPrompted: Partial<Record<AccountMoment, number>> = {};
    try {
      const raw = localStorage.getItem(PROMPT_KEY);
      if (raw !== null) lastPrompted = JSON.parse(raw) as Partial<Record<AccountMoment, number>>;
    } catch {
      // Stockage illisible ou JSON corrompu : on repart d'une ardoise vierge. Au pire on
      // propose une fois de trop, ce qui vaut mieux que de ne plus jamais proposer.
    }
    // La section connaît déjà la réponse : elle l'affiche. Lui demander évite une
    // seconde lecture de la même vérité, qui finirait par la contredire (règle 22).
    return { linked: account.isLinked(), lastPrompted };
  }

  function offerAccount(moment: AccountMoment): void {
    /*
      Sans configuration Supabase, `createAccountSection` s'EFFACE — elle refuse d'afficher
      un formulaire qui échouera. L'invitation s'ouvrirait alors sur une boîte ne contenant
      qu'un texte et un bouton « Plus tard » : le pire des deux mondes, un dérangement sans
      la contrepartie. Elle ne s'ouvre donc pas du tout.

      ⚠️ Cette garde est aussi ce qui empêche `probe:invite` d'être verte pour rien : sans
      elle, la sonde voyait la boîte s'ouvrir et n'avait aucun moyen de savoir qu'elle
      était creuse (règle 23).
    */
    if (!isSupabaseConfigured()) return;

    const now = Date.now();
    if (!shouldPromptAccount(moment, readPromptState(), now)) return;

    const next = markPrompted(readPromptState(), moment, now);
    try {
      localStorage.setItem(PROMPT_KEY, JSON.stringify(next.lastPrompted));
    } catch {
      // Voir ci-dessus : perdre le compteur ne casse rien, il se reconstruira.
    }
    invite.open(moment);
  }
  // Chaque ligne DIT sa section : l'ordre de ce fichier ne décide plus de la mise en
  // page des réglages.
  /*
    ── LA MESURE DÉMARRE ICI, PAS PLUS TÔT (ROADMAP 9.1) ─────────────────────

    Après le montage, jamais dans le chemin critique : le §1 promet un jeu jouable en
    moins de cinq secondes, et une bibliothèque d'analytics chargée avant le premier rendu
    est le contraire de ça. `startAnalytics` importe PostHog de façon DYNAMIQUE.

    L'identité est celle que le jeu possède déjà — l'`auth.uid()` de la session anonyme,
    qui existe pour sauvegarder la ville. Aucun stockage n'est créé pour la mesure.
  */
  /*
    ⚠️ LA MESURE NE DÉPEND PAS DE LA SESSION. Elle démarre TOUT DE SUITE, et l'identité la
    rejoint quand elle arrive — si elle arrive.

    Première version : les deux appels étaient dans le `.then` de `ensureAuthSession`.
    Mesuré par `probe:analytics` — `game_started` ne partait JAMAIS, parce que la session
    anonyme échouait (HTTP 429, la limite de connexions par heure). Le dénominateur de tout
    l'entonnoir manquait, et rien ne le signalait : les requêtes vers PostHog partaient
    bien, mais aucune ne portait d'événement.

    C'est la règle 1 du module retournée contre moi : la mesure ne doit dépendre de rien
    qui puisse échouer, pas même de ce qui marche « presque toujours ».
  */
  /*
    ⚠️ APRÈS LE PREMIER RENDU, mesuré et non supposé.

    Première version : l'appel était ici, en pleine construction — et `probe:analytics` a
    compté DEUX requêtes vers PostHog avant que le canvas existe. C'est exactement la
    règle 2 de ce module, violée par celui qui l'a écrite.

    Deux crans d'attente plutôt qu'un : `requestAnimationFrame` garantit qu'une image est
    passée, `requestIdleCallback` que le navigateur n'a plus rien d'urgent. Sur les
    navigateurs sans `requestIdleCallback` (Safari ancien), le repli est un délai court —
    jamais rien, sinon la mesure ne partirait jamais sur ces machines.
  */
  const startMeasuring = (): void => {
    startAnalytics(null);
    track(EVENTS.gameStarted, { era: store.getSnapshot().prestigeLevel });

    /*
      ⚠️ `currentAuthSession`, PAS `ensureAuthSession` — et la différence est celle entre
      reconnaître et inscrire.

      `ensureAuthSession()` minte un compte anonyme quand il n'y en a pas. L'appeler ici
      faisait créer un compte à CHAQUE ouverture du jeu, avant tout geste du joueur, au
      seul motif d'attacher un identifiant à des événements. C'était la source la plus
      abondante des ~380 villes distantes par jour — et le verrou posé sur la synchro n'y
      pouvait rien, puisque la synchro n'était pas la coupable. `probe:sync` l'a vu : un
      `signup` partait sur une ville vide, verrou en place.

      La mesure part de toute façon sans identité (`startAnalytics(null)` juste au-dessus).
      Elle en gagne une si le joueur en a déjà une ; elle ne lui en fabrique pas.
    */
    void currentAuthSession()
      .then((playerId) => {
        if (playerId !== null) startAnalytics(playerId);
      })
      .catch(() => undefined);
  };

  /*
    ⚠️ LES DEUX EN COURSE, et le délai n'est PAS un repli.

    `requestIdleCallback` ne se déclenchait JAMAIS : un idle game rend en continu, donc le
    navigateur n'est jamais au repos. Mesuré — la mesure ne partait plus du tout, et la
    sonde annonçait « pas de clé » sur un build qui en avait une. Un correctif de
    performance qui supprime la fonctionnalité n'est pas un correctif.

    Le premier des deux gagne : l'inactivité si elle vient, le délai sinon. `started`
    empêche le second de repasser derrière le premier.
  */
  let started = false;
  const startOnce = (): void => {
    if (started) return;
    started = true;
    startMeasuring();
  };

  requestAnimationFrame(() => {
    const idle = (globalThis as { requestIdleCallback?: (cb: () => void) => void })
      .requestIdleCallback;
    idle?.(startOnce);
    globalThis.setTimeout(startOnce, 1200);
  });

  settings.appendRow(account.el, 'account');
  /*
    ── LA CARTE LUDOPIA, À CÔTÉ DU COMPTE ET NON À SA PLACE ──────────────────

    Deux identités distinctes, et l'écran doit le montrer : le compte Villopia possède la
    ville et la sauvegarde, le compte Ludopia porte un pseudo, un avatar et des Ludos qui
    traversent les jeux. Les présenter comme une seule chose ferait croire qu'en perdant
    l'un on perd l'autre — faux, et inquiétant au moment précis où l'on demande à
    quelqu'un de se connecter.
  */
  const ludopia = createLudopiaSection();
  settings.appendRow(ludopia.el, 'account');
  settings.appendRow(versionInfo.row, 'about');
  shopHost.append(versionInfo.chip);

  /*
    Installation sur l'écran d'accueil — et pourquoi il faut DEUX chemins.

    Sur Android, Chromium émet `beforeinstallprompt` et on peut ouvrir la boîte native.
    Sur iOS il n'existe aucune API : l'installation passe par le geste
    « Partager → Sur l'écran d'accueil », que rien n'indiquait. Les balises Apple étaient
    pourtant correctes dans `index.html` — l'installation marchait, mais aucun joueur
    iPhone ne pouvait le deviner.

    La ligne des réglages est le chemin PERMANENT ; la bannière est le chemin
    DÉCOUVRABLE, et elle attend 25 s pour que la ville ait été vue tourner d'abord (E§4).
  */
  const installRow = createInstallRow();
  settings.appendRow(installRow.el, 'preferences');

  /*
    ── Son, crédits, remise à zéro — trois manques relevés d'un bloc ──────────

    Le moteur audio porte ses réglages depuis l'étape 4a, avec leur persistance, et rien
    ne les exposait : un jeu qui sonne à chaque pièce produite et qu'on ne peut pas
    baisser se ferme. Les crédits, eux, sont une obligation contractuelle des licences
    Synty et Kenney — une obligation qui n'existe que dans un fichier du dépôt n'est pas
    remplie. Et le joueur ne pouvait recommencer qu'en vidant son navigateur, ce qui
    emporte aussi sa session, donc son adresse publique.
  */
  const preferences = createPreferences({
    sound,
    onReset: () => {
      /*
        On efface la SAUVEGARDE, jamais la session.

        Vider tout le stockage local emporterait le jeton d'authentification anonyme :
        le joueur perdrait son adresse publique, donc les liens déjà partagés, et se
        retrouverait avec une deuxième ville distante orpheline. Recommencer une partie
        n'a jamais voulu dire changer d'identité.

        Le rechargement plutôt qu'une réinitialisation en mémoire : une ville neuve
        touche l'état, le rendu, la caméra, les quêtes et la synchro. Rejouer le
        démarrage est la seule façon d'être sûr qu'aucun morceau ne garde l'ancienne.
      */
      clearSnapshot();
      location.reload();
    },
  });
  /*
    Chaque ligne va dans SA section, lue sur son propre `data-setting` plutôt que sur son
    rang dans le tableau : les crédits sont une information qu'on consulte, pas un réglage
    qu'on change — ils vivaient sous « Préférences » avec un libellé « À propos », juste
    au-dessus d'une section qui s'appelle « À PROPOS ».
  */
  for (const preferenceRow of preferences.rows) {
    const isAbout = preferenceRow.dataset.setting === 'credits';
    settings.appendRow(preferenceRow, isAbout ? 'about' : 'preferences');
  }

  const installBanner = createInstallBanner();
  shopHost.append(installBanner.el);
  installBanner.schedule(Date.now());

  /*
    « Nouvelle version — Rafraîchir » : le maillon qui manquait à la chaîne de mise à jour.

    La 0.10.4 a empêché le serveur de répondre du HTML pour un morceau absent, la 0.10.5 a
    fait passer le document en `NetworkFirst`. Il restait un trou au milieu : le service
    worker se met à jour en silence et la page continue de tourner sur l'ANCIEN code
    jusqu'au prochain rechargement. Un idle se joue en laissant l'onglet ouvert — un
    correctif déployé n'atteignait donc jamais le joueur en session.

    Invisible en développement : aucun service worker n'y est enregistré
    (`devOptions.enabled: false`). Se vérifie en production, ce que la règle 11 impose.
  */
  const updateNotice = createUpdateNotice();
  // Monté sur `#shop-host` et non sur le HUD : ce dernier n'existe pas encore à ce
  // point du démarrage, et l'hôte couvre déjà tout l'écran — l'avis se positionne
  // lui-même en absolu sous la barre.
  shopHost.append(updateNotice.el);
  const stopWatchingUpdates = watchForUpdates(() => {
    updateNotice.show();
  });

  /*
    ROADMAP 3.6 — l'ouverture d'une ère se DIT, là où le joueur regarde.

    Elle ne vivait que dans le panneau Progression, donc derrière un tap qu'on n'a
    aucune raison de faire à cet instant précis : on est en train de poser des
    bâtiments. Or c'est le plus gros objectif de la partie, et le §1 exige qu'il soit
    sous les yeux. Le bandeau est monté sur le même hôte que l'avis de mise à jour, et
    se place tout seul au-dessus du dock.
  */
  /*
    ⚠️ **UNE DESTINATION EST UNE SECTION *ET* UN ONGLET** (ROADMAP 3.1).

    Depuis que Progression est découpée, `shell.select('progress')` ne suffit plus : il
    dépose le joueur sur l'onglet Quêtes, quel que soit ce qu'on lui avait promis. Un
    bandeau qui annonce une ère et mène à des quêtes est pire qu'un bandeau muet — il
    fait perdre le geste ET la confiance.

    Déclarée en `function` et non en `const` : elle est référencée par des rappels
    définis avant `progressTabs`, et n'est appelée qu'après — la remontée de déclaration
    est ici la façon la plus simple de dire « ce lien se résout au moment du clic ».
  */
  function goToProgress(tab: 'quests' | 'milestones' | 'eras'): void {
    shell.select('progress');
    progressTabs.select(tab);
  }

  const eraNotice = createEraNotice({
    onOpen: () => {
      goToProgress('eras');
    },
  });
  shopHost.append(eraNotice.el);

  /*
    ROADMAP 3.5 — Bricolo permanent.

    Il ne décide de rien : `core/advisor` répond à « qu'est-ce que je vise, là ? », et ce
    module l'emmène là où l'action se fait. Chaque sorte de conseil connaît sa
    destination — un conseil sur lequel on tape et qui ne mène nulle part serait pire
    que pas de conseil.
  */
  const companion = createCompanion({
    onAct: (advice) => {
      // Deux conseils mènent à Progression, et pas au même endroit : une quête
      // encaissable est sous Quêtes, une ère ouverte sous Ères.
      if (advice.kind === 'quest') {
        goToProgress('quests');
        return;
      }
      if (advice.kind === 'era') {
        goToProgress('eras');
        return;
      }

      const destination: Record<AdviceKind, 'build' | 'city' | 'social' | 'progress'> = {
        quest: 'progress',
        era: 'progress',
        bag: 'build',
        roads: 'city',
        land: 'city',
        build: 'build',
        unlock: 'build',
        share: 'social',
      };
      shell.select(destination[advice.kind]);
    },
  });
  shopHost.append(companion.el);

  /*
    ⚠️ **Le conseil se recalcule au plus une fois par seconde et demie, pas dix fois.**

    `orphanRoadCount` SIMULE le nettoyage — il rappelle `removeRoad` case par case, avec
    un parcours de connexité à chaque essai. À 10 Hz, ce serait la passe la plus chère du
    jeu pour une phrase qui ne change qu'à l'échelle de la minute.
  */
  let lastAdviceAt = 0;
  let lastAdvice: Advice | null = null;
  /*
    ⚠️ NEUF SECONDES, et non une et demie.

    « La bulle en bas à gauche défile beaucoup beaucoup trop vite » (Benoit, 21 août), et
    c'était mesurable : 1 500 ms. Une phrase de conseil fait dix à quinze mots ; on en lit
    trois par seconde en lecture attentive, moins en jouant d'un œil. Le texte partait donc
    avant d'avoir été lu, et le compagnon devenait un scintillement au lieu d'un conseil.

    Neuf secondes laissent le temps de lire, de décider, et même d'ignorer — ce qui est
    aussi une réponse. Le conseil change de toute façon dès que l'état change : ce délai
    est un PLANCHER, pas une cadence.
  */
  const ADVICE_EVERY_MS = 9000;

  /**
   * Le tiroir est-il ouvert par-dessus la bande où vit le compagnon ?
   *
   * ⚠️ **Mesuré sur le DOM, et surtout PAS sur le compagnon lui-même.** Première
   * version : on croisait sa boîte avec celle du tiroir. Or un élément caché n'a plus de
   * boîte — la mesure rendait alors « non recouvert », le compagnon réapparaissait, et
   * au tour suivant il était de nouveau recouvert. Une oscillation à 10 Hz, invisible en
   * lecture et parfaitement visible à l'écran.
   *
   * On compare donc deux éléments qui existent TOUJOURS : le tiroir et le dock. Si le
   * tiroir monte au-dessus du dock, il occupe la bande basse — celle du compagnon. Aucun
   * seuil de largeur écrit à la main : la mise en page peut changer, la question reste
   * juste.
   *
   * Le défaut mesuré au format téléphone : carte de boutique centrée en (247, 649),
   * compagnon de 636 à 684, `elementFromPoint` rendant `span.companion__text` — le
   * conseil volait le tap qu'il conseillait de faire.
   */
  function companionCovered(): boolean {
    if (shell.isDesktop()) return false;

    const sheet = document.querySelector('.v-sheet');
    const nav = document.querySelector('.v-nav');
    if (!(sheet instanceof HTMLElement) || !(nav instanceof HTMLElement)) return false;
    if (sheet.hidden) return false;

    const sheetBox = sheet.getBoundingClientRect();
    const navBox = nav.getBoundingClientRect();
    if (sheetBox.height === 0 || navBox.height === 0) return false;

    return sheetBox.top < navBox.top;
  }

  function refreshAdvice(snapshot: CitySnapshot, now: number): void {
    // Le recouvrement se relit à CHAQUE passe, pas seulement au changement de conseil :
    // le joueur ouvre et referme le tiroir sans que le conseil bouge.
    companion.setCovered(companionCovered());

    if (now - lastAdviceAt < ADVICE_EVERY_MS) return;
    lastAdviceAt = now;
    const advice = nextAdvice(snapshot, now, lastAdvice?.kind);
    lastAdvice = advice;
    companion.show(advice);
  }

  /*
    Section « Social » — le partage (§9).

    L'entrée du dock existait depuis le début et ne montait AUCUN contenu : taper
    « Social » n'affichait rien, alors que c'est la moitié du concept. Le lien vient du
    slug généré par le serveur : la section s'ouvre donc sur un état d'attente et se
    remplit à la première synchro.

    `location.origin` et non un domaine écrit en dur : un lien partagé doit pointer vers
    l'hôte qui l'a produit. Le jour où `villopia.app` servira le jeu, c'est ici qu'on
    forcera le domaine canonique — pas avant, un lien vers un domaine qui ne répond pas
    étant pire que pas de partage du tout.
  */
  const socialSection = createSocialSection({
    origin: location.origin,
    onNotice: (message) => {
      toast.show({ title: message });
    },
    claimedSocial: () =>
      SOCIAL_CHANNELS.filter((channel) =>
        hasClaimedSocial(store.getSnapshot().claimedMilestones, channel),
      ),
    /*
      ── DIX GEMMES PAR RÉSEAU, UNE SEULE FOIS (ROADMAP 2.6) ──────────────────

      L'idempotence vit dans `claimedMilestones`, qui voyage dans le snapshot : réclamer
      deux fois sur deux appareils ne marche pas non plus. Vider son stockage local pour
      recommencer est possible — et coûte la ville entière. Trente gemmes contre une
      partie : on ne se protège pas d'un abus qui coûte plus cher qu'il ne rapporte.
    */
    onSocialVisit: (channel) => {
      const before = store.getSnapshot();
      if (hasClaimedSocial(before.claimedMilestones, channel)) return 0;

      store.setSnapshot({
        ...before,
        gems: before.gems + SOCIAL_REWARD_GEMS,
        claimedMilestones: [...before.claimedMilestones, socialClaimId(channel)],
        updatedAt: Date.now(),
      });
      hud.celebrateGems();
      sound.play('milestone');
      toast.show({
        title: t('social.rewarded', { value: n(SOCIAL_REWARD_GEMS) }),
        text: t('social.rewardedText', {
          value: n(remainingSocialGems(store.getSnapshot().claimedMilestones)),
        }),
        icon: 'gem',
        domain: 'gems',
      });
      return SOCIAL_REWARD_GEMS;
    },
    // Moment 1 (6.2) — son lien vient de partir : c'est l'instant où « ta ville te suit
    // partout » a le plus de sens, parce qu'elle vient d'exister pour quelqu'un d'autre.
    onShared: () => {
      offerAccount('shared');
    },
  });

  /*
    Synchronisation de la ville vers Supabase (étape 10). Entièrement en arrière-plan :
    le jeu vit en local, l'ombre distante suit. C'est elle qui donne un slug public à
    la ville — la matière première du partage (§9).
  */
  /**
   * Boost de visite actif, produit des boosts serveur non expirés (§9).
   *
   * ── Pourquoi il est RELU au serveur, et jamais calculé ici ─────────────────
   *
   * Un boost naît d'une visite, donc d'un fait qui s'est produit chez quelqu'un d'autre.
   * `boosts` est en lecture publique et en écriture strictement `service_role` : un
   * joueur qui éditerait sa sauvegarde locale ne se fabriquerait aucun boost. C'est
   * exactement la frontière que le §14 demande — le temps de jeu et le partage donnent
   * de la production, la console du navigateur non.
   *
   * Relu toutes les deux minutes, pas à chaque tick : un boost dure deux heures, et
   * interroger le réseau dix fois par seconde pour un nombre qui change à peine serait
   * absurde.
   */
  /**
   * Types déjà signalés comme abordables — la mémoire des pastilles du dock.
   *
   * Sans elle, la pastille de « Construire » afficherait en permanence le nombre de
   * bâtiments payables, c'est-à-dire presque toujours le même nombre : un signal
   * constant n'est plus un signal. On ne notifie que la TRANSITION vers l'abordable.
   */
  const seenAffordable = new Set<string>();

  let visitBoost = 1;
  /** Fin du dernier boost de visite — le compte à rebours du HUD (demande de Benoit). */
  let visitBoostUntil = 0;
  let boostTimer = 0;

  /*
    ⚠️ **Deux nombres, pas un** — et l'oubli du second a tué la moitié de la boucle.

    Le boost et le COMPTEUR de visites sont deux faits distincts. Le boost est temporaire
    et se lit dans `boosts` ; le compteur est cumulatif et se lit dans `daily_stats`.
    Seul le premier était sondé. Résultat exactement conforme au symptôme remonté par le
    joueur : le lien marche, la notification arrive, la production accélère — **et le
    compteur reste à zéro**.

    Ce n'était pas qu'un chiffre figé. `visitorsReceived` alimente aussi les quarante
    paliers de jalons de visiteurs et les quêtes de visite : tout ce que le partage
    devait rapporter, hors boost, ne se déclenchait jamais. D'où le passage par
    `applyWithMilestones`, et non par un simple `setSnapshot` — c'est lui qui encaisse.

    Les deux requêtes partent ENSEMBLE (`Promise.all`) : elles sont indépendantes, et
    les enchaîner doublerait la latence d'un sondage qui a déjà lieu toutes les deux
    minutes.
  */
  const refreshVisits = (cityId: string): void => {
    void Promise.all([activeBoost(cityId, Date.now()), visitsReceived(cityId)]).then(
      ([boost, received]) => {
        const value = boost.multiplier;
        const changed = value !== visitBoost;
        visitBoost = value;
        visitBoostUntil = boost.until;
        // Un boost qui apparaît est une NOUVELLE, pas un changement de chiffre : le joueur
        // doit apprendre que quelqu'un est passé chez lui.
        if (changed && value > 1) {
          sound.play('milestone');
          toast.show({
            title: t('visits.received'),
            text: t('visits.boostActive', { value: n(value) }),
            icon: 'eye',
            domain: 'social',
          });
        }
        socialSection.setBoost(value, boost.until);

        const snapshot = store.getSnapshot();
        const counted = recordVisitors(snapshot, received);
        // `recordVisitors` rend le snapshot TEL QUEL si rien ne change : sonder toutes
        // les deux minutes ne provoque donc ni rendu ni sauvegarde inutile.
        if (counted !== snapshot) {
          refreshHud(applyWithMilestones(counted));
          // D.1 — de nouveaux visiteurs, donc de nouvelles traces, avec leur fanfare.
          spawnTraces();
        } else if (changed) refreshHud(snapshot);
      },
    );
  };

  const sync = createSync({
    getSnapshot: () => store.getSnapshot(),
    defaultName: t('city.defaultName'),
    /*
      ══ ON NE CRÉE PAS UNE VILLE DISTANTE POUR RIEN ═══════════════════════════

      Deux verrous, et chacun ferme une source distincte des ~380 villes par jour :

      1. **Le joueur doit avoir POSÉ quelque chose.** Une ville vide n'a rien à
         sauvegarder, rien à partager, et rien à classer — la créer donne un compte
         anonyme, un slug public et une ligne en base à quelqu'un qui est déjà reparti.
         C'est aussi le critère exact que la purge et le classement emploient côté
         serveur (`total_production > 0`) : une seule notion de « ville vivante », pas
         trois.

      2. **`?debug` ne synchronise pas.** Toutes les sondes ouvrent le jeu ainsi, et
         plusieurs POSENT des bâtiments — `probe:decor`, `probe:clearing`, `make:og`.
         Le premier verrou seul ne les arrêterait donc pas. Un joueur, lui, n'a aucune
         raison d'avoir `?debug` dans son adresse.

      ⚠️ `probe:viral` a besoin de la synchro : c'est tout son sujet. Elle demande
      `?debug&sync`, une opt-in explicite — sans quoi on aurait éteint la sonde en même
      temps que le bruit qu'elle mesure.
    */
    worthCreating: () => {
      const params = new URLSearchParams(location.search);
      if (params.has('debug') && !params.has('sync')) return false;
      return activeIsland(store.getSnapshot()).buildings.length > 0;
    },
    onCity: (city) => {
      // La session vient peut-être de naître : l'écran Compte doit refléter l'identité.
      account.refresh();
      // Le slug est la matière première du partage : la section Social attendait ça.
      socialSection.setCity(city);
      // Le nom et l'adresse ouvrent la section Ville — c'est l'identité du joueur.
      citySection.setCity(city);

      // La contrepartie du partage : on va chercher ce que les visites ont rapporté.
      refreshVisits(city.id);
      socialSection.setBoost(visitBoost, visitBoostUntil);
      globalThis.clearInterval(boostTimer);
      boostTimer = window.setInterval(() => {
        refreshVisits(city.id);
        socialSection.setBoost(visitBoost, visitBoostUntil);
      }, 120_000);
    },
  });
  // Boutique de gemmes, ouverte depuis le compteur : c'est là que le joueur regarde
  // quand il en manque.
  /*
    La boutique de gemmes n'est plus seulement un endroit où en ACHETER : c'est d'abord
    l'endroit où en DÉPENSER. Sans ces deux rappels, les gemmes gagnées par les quêtes et
    les jalons ne s'échangeaient contre rien — l'audit du 17 août a compté zéro site de
    dépense dans tout le dépôt.
  */
  const store_ = createStorePanel(() => getLocale(), {
    onRush: (tierId: string) => {
      const result = buyRush(store.getSnapshot(), tierId, Date.now());
      if (!result.ok) {
        sound.play('refused');
        hud.refuse();
        if (result.refusal === 'too-few-gems') {
          toast.show({ title: t('gems.tooFew'), text: t('gems.tooFewHint') });
        }
        return;
      }
      store.setSnapshot(result.snapshot);
      sound.play('milestone');
      // Le toast nomme le palier ACHETÉ, pas un palier de catalogue : trois offres
      // existent désormais, et annoncer la mauvaise serait pire que se taire.
      const tier = GEM_SPENDING.rush.find((entry) => entry.id === tierId);
      toast.show({
        title: t('gems.rushBought'),
        ...(tier === undefined
          ? {}
          : {
              text: t('gems.rushTier', {
                value: n(tier.multiplier),
                duration: duration(tier.durationMs),
              }),
            }),
      });
    },
    /*
      ── L'AVANCE DE PRODUCTION (G§3, A1) ────────────────────────────────────

      Le refus « quota du jour épuisé » se dit autrement que « pas assez de gemmes » :
      l'un se règle en revenant demain, l'autre en jouant aujourd'hui. Un même toast
      pour les deux enverrait la moitié des joueurs au mauvais endroit.

      Le cas d'une ville qui ne produit RIEN est refusé côté commande (`coins <= 0`
      n'y est pas possible, mais `currentProduction` peut valoir zéro) : encaisser
      15 gemmes contre zéro pièce serait un vol, même involontaire.
    */
    onSkip: (tierId: string) => {
      const snapshot = store.getSnapshot();
      if (currentProduction(snapshot) <= 0) {
        sound.play('refused');
        hud.refuse();
        toast.show({ title: t('gems.skipEmpty'), domain: 'alert' });
        return;
      }

      const result = buySkip(snapshot, tierId, Date.now());
      if (!result.ok) {
        sound.play('refused');
        hud.refuse();
        toast.show(
          result.refusal === 'max-reached'
            ? { title: t('gems.skipExhausted') }
            : { title: t('gems.tooFew'), text: t('gems.tooFewHint') },
        );
        return;
      }

      store.setSnapshot(result.snapshot);
      sound.play('milestone');
      // La pluie de pièces, comme au retour hors-ligne : c'est le même geste — du temps
      // transformé en pièces — et il doit se VOIR (E§1.1).
      renderer.rainCoins(result.coins, performance.now());
      toast.show({
        title: t('gems.skipBought'),
        text: `+${n(result.coins)}`,
        icon: 'coin',
        domain: 'coins',
      });
    },
    onNight: () => {
      const result = buyNightPass(store.getSnapshot(), Date.now());
      if (!result.ok) {
        sound.play('refused');
        hud.refuse();
        if (result.refusal === 'too-few-gems') {
          toast.show({ title: t('gems.tooFew'), text: t('gems.tooFewHint') });
        }
        return;
      }
      store.setSnapshot(result.snapshot);
      sound.play('milestone');
      toast.show({ title: t('gems.nightBought'), text: t('gems.night') });
    },
    onLand: () => {
      const result = buyLandWithGems(store.getSnapshot(), Date.now());
      if (!result.ok) {
        sound.play('refused');
        hud.refuse();
        if (result.refusal === 'too-few-gems') {
          toast.show({ title: t('gems.tooFew'), text: t('gems.tooFewHint') });
        }
        return;
      }
      sound.playPlacement();
      toast.show({ title: t('gems.landBought') });
      const applied = applyWithMilestones(result.snapshot);
      renderer.setGridTo(activeIsland(applied).grid);
      refreshProps();
    },
    onBuy: (packId) => {
      // Moment 3 (6.2) — AVANT le paiement, pas après : une fois sur la page de Stripe,
      // le joueur est parti, et lui redemander au retour arriverait trop tard.
      offerAccount('purchase');
      void startCheckout(packId).then((outcome) => {
        if (outcome === 'unavailable') {
          toast.show({ title: t('store.soonTitle'), text: t('store.soon') });
        } else if (outcome === 'error') {
          toast.show({ title: t('store.failed'), domain: 'alert' });
        }
        // 'redirected' : la page part chez Stripe, rien à dire ici.
      });
    },
    onWatchAd: () => {
      /*
        Le contrat des rewarded ads : la récompense ne part QUE sur un visionnage
        COMPLET (`true`). Le quota du jour se revérifie À LA RÉCOMPENSE, pas
        seulement au bouton — entre les deux, un autre onglet a pu consommer le
        dernier visionnage.
      */
      void showRewardedAd().then((outcome) => {
        if (outcome !== 'rewarded') {
          /*
            Deux échecs, deux phrases. « Interrompue » accuse le joueur d'avoir
            fermé la pub ; quand la régie n'avait simplement rien à servir — le cas
            courant tant qu'AdSense n'a pas approuvé le site — c'est faux, et un
            reproche injuste vaut moins qu'un silence.
          */
          toast.show({
            title: outcome === 'dismissed' ? t('ads.aborted') : t('ads.unavailable'),
          });
          return;
        }
        const now = Date.now();
        const snapshot = store.getSnapshot();
        const today = dayNumber(now);
        const watched = snapshot.adsDay === today ? snapshot.adsWatched : 0;
        if (watched >= REWARDED_AD.dailyCap) return;

        /*
          La pub écrit dans SON emplacement (`grantBoost`, écrivain unique), et non
          plus dans le champ unique partagé avec l'accélération payante. Les deux se
          cumulent donc au lieu de s'évincer — G§3 A3.
        */
        store.setSnapshot({
          ...grantBoost(snapshot, 'ad', REWARDED_AD.multiplier, REWARDED_AD.durationMs, now),
          adsDay: today,
          adsWatched: watched + 1,
          updatedAt: now,
        });
        sound.play('milestone');
        toast.show({
          title: t('ads.rewarded'),
          text: t('gems.rush', {
            value: n(REWARDED_AD.multiplier),
            duration: duration(REWARDED_AD.durationMs),
          }),
          icon: 'bolt',
        });
      });
    },
  });
  const hud = createHud(
    () => {
      settings.open();
    },
    () => {
      store_.open();
    },
  );
  resourcesHost.append(hud.el);
  resourcesHost.hidden = false;

  const toast = createToast({ title: '', icon: 'gem', domain: 'gems' });
  const toastLayer = document.createElement('div');
  // Sous le bloc de ressources, jamais dessus : une célébration qui masque le compteur
  // de pièces cache précisément ce qu'elle vient de faire bouger. La position vient de
  // `--hud-height`, publiée par le bandeau lui-même — elle valait 172 px en dur, ce qui
  // était juste sur une seule mise en page (audit § U4).
  toastLayer.className = 'toast-layer';
  toastLayer.append(toast.el);
  shopHost.append(toastLayer);

  /* ── Boutique et placement ─────────────────────────────────────────── */

  const shop = createShop({
    onSelect: (type) => {
      // Le palier vient de l'ÉTAT : le fantôme doit montrer le modèle qui sera
      // réellement posé, pas systématiquement le palier 1 (audit — aperçu menteur).
      /*
        ⚠️ Un doigt DÉJÀ posé est honoré quand le fantôme arrive.

        `setGhost` télécharge ses textures : en local c'est instantané, en production
        c'est le réseau. Un joueur qui choisit un bâtiment puis pose immédiatement le
        doigt appuyait donc dans le vide — l'appui tombait pendant le chargement, le
        fantôme était encore `null`, et rien n'apparaissait avant le premier mouvement.
        Invisible sur le poste de dev, mesuré en production par `probe:placement` : le
        même code, seul le réseau change (règle 11).
      */
      void renderer.setGhost(type === '' ? null : type, ghostLevelFor(type)).then(() => {
        if (tap !== null && renderer.getGhostType() !== null) {
          const rect = canvas.getBoundingClientRect();
          const cell = renderer.cellAtScreen(tap.x - rect.left, tap.y - rect.top);
          const island = activeIsland(store.getSnapshot());
          const type2 = renderer.getGhostType() ?? '';
          renderer.moveGhostTo(cell, cell !== null && canPlace(island, type2, cell.col, cell.row));
        }
      });
      shopHost.classList.toggle('is-placing', type !== '');
      setPlacementMode(type !== '');
    },
    onAuto: (type, quantity) => {
      commitMany(type, quantity);
    },
    /*
      ROADMAP 3.3 — le déblocage se CÉLÈBRE. L'illumination de la carte ne se voit que
      si le tiroir est ouvert et la carte à l'écran ; or on débloque souvent en posant
      un bâtiment, donc en regardant la ville. Le toast, lui, arrive toujours.
    */
    onUnlocked: (type) => {
      sound.play('milestone');
      toast.show({
        /*
          Le NOM vit dans le texte, pas dans le titre : « Chaumière débloqué » — le
          catalogue mélange les genres, et un accord par bâtiment coûterait dix clés
          dans deux langues pour une information dont l'anglais n'a que faire.
        */
        title: t('shop.unlocked'),
        text: t('shop.unlockedText', { value: t(`buildings.${type}` as TranslationKey) }),
        icon: 'build',
        domain: 'coins',
      });
    },
    /*
      Il ne manque QUE des gemmes : on le dit, puis on mène à la boutique. L'ordre compte —
      une boutique qui s'ouvre sans un mot se lit comme un défaut, pas comme une offre.
    */
    onShortOnGems: (type, missing) => {
      sound.play('refused');
      hud.refuse();
      toast.show({
        title: t('shop.shortOnGems', { value: n(missing) }),
        text: t('shop.shortOnGemsHint', {
          value: t(`buildings.${type}` as TranslationKey),
        }),
        icon: 'gem',
        domain: 'gems',
      });
      store_.open('buy');
    },

    onRefused: (type) => {
      // E§1.3 : le corps de l'interface répond, jamais une boîte de dialogue.
      // Ne concerne plus que les cartes SCELLÉES : une carte débloquée mais trop chère
      // se sélectionne et propose de compléter en gemmes (ROADMAP 1.3).
      sound.play('refused');
      hud.refuse();

      /*
        ── ET ON DIT POURQUOI ────────────────────────────────────────────────

        « Pas d'alertes pour dire : tu dois acheter 5 maisons avant de construire une
        chaumière, et pour tous les autres » (Benoit, 21 août). Le refus était MUET : un
        son, une secousse, et le joueur devait deviner la règle — ou lire l'indice en tout
        petit sur la carte, qu'on ne lit pas quand on vient de taper dessus.

        Le message est DÉRIVÉ de `unlockProgress`, jamais recopié : il nomme le bâtiment
        précédent, dit combien il en faut et combien on en a. Une phrase écrite en dur
        aurait menti au premier changement de catalogue, et il aurait fallu la traduire dix
        fois, une par bâtiment.

        ⚠️ Le « × » n'est pas décoratif : il ÉVITE L'ACCORD. Le catalogue mélange les
        genres et les nombres — « 5 Maison » est faux, « 5 Maisons » demanderait une forme
        plurielle par bâtiment dans deux langues. « 5 × Maison » se lit juste dans tous les
        cas, et c'est déjà l'idiome du jeu (×1, ×10, ×MAX).
      */
      const gate = unlockProgress(
        buildingCounts(store.getSnapshot()),
        type,
        store.getSnapshot().prestigeLevel,
      );
      if (gate === null) return;

      toast.show({
        title: t('shop.lockedTitle'),
        text: t('shop.lockedHint', {
          need: gate.need,
          owned: gate.owned,
          previous: t(`buildings.${gate.previousType}` as TranslationKey),
          value: t(`buildings.${type}` as TranslationKey),
        }),
        icon: 'build',
        domain: 'alert',
      });
    },

    /*
      ══ 1.3 — le prix du complément, dérivé et jamais recalculé ici ══════════

      `game.ts` ne connaît pas le barème : il donne à `core/topUp` la CIBLE — le prix du
      prochain exemplaire — et lui laisse dériver le reste depuis l'échelle d'avance de
      production. L'interface affiche donc exactement ce qui sera encaissé.
    */
    quoteTopUp: (type) => {
      const snapshot = store.getSnapshot();
      const definition = BUILDINGS.find((entry) => entry.type === type);
      if (definition === undefined) return null;
      const owned = buildingCounts(snapshot)[type] ?? 0;
      return quoteTopUp(snapshot, nextCost(definition, owned), Date.now());
    },

    /*
      Le seul chemin quand le solde de gemmes ne suffit pas (ROADMAP 2.1). La boutique
      s'ouvre par le MÊME appel que la pastille du bandeau — deux portes vers un seul
      écran, jamais deux écrans qui se ressemblent.
    */
    onNeedGems: () => {
      store_.open('buy');
    },

    /*
      Achat d'une décoration (2.4, refondu par 2.9a et 2.9c). L'achat encaisse et met
      l'objet au SAC À DOS, puis arme le mode de pose au fantôme — le même geste de
      visée que les bâtiments. Annuler ne perd rien : l'objet attend au sac. Le refus
      « pas de place » a disparu — c'est le joueur qui vise.
    */
    onBuyDecoration: (id) => {
      const result = buyDecoration(store.getSnapshot(), id, Date.now());
      if (!result.ok) {
        sound.play('refused');
        hud.refuse();
        // Manque de gemmes : on MÈNE à la boutique plutôt que de refuser (2.1).
        if (result.refusal === 'too-few-gems') store_.open('buy');
        else toast.show({ title: t(`decorRefused.${result.refusal}`) });
        return false;
      }

      store.setSnapshot(result.snapshot);
      sound.play('purchase');
      startDecorPlacement(result.item.id);
      return true;
    },

    // Poser (ou reposer) un objet du sac à dos — après un prestige, ou une pose annulée.
    onPlaceDecoration: (itemId) => {
      startDecorPlacement(itemId);
    },

    onTopUp: (type) => {
      const snapshot = store.getSnapshot();
      const definition = BUILDINGS.find((entry) => entry.type === type);
      if (definition === undefined) return false;
      const owned = buildingCounts(snapshot)[type] ?? 0;

      const result = topUpCoins(snapshot, nextCost(definition, owned), Date.now());
      if (!result.ok) {
        sound.play('refused');
        hud.refuse();
        toast.show({ title: t(`shop.topUpRefused.${result.refusal ?? 'invalid'}`) });
        return false;
      }

      store.setSnapshot({ ...result.snapshot, updatedAt: Date.now() });
      sound.play('purchase');
      hud.celebrateGems();
      toast.show({
        title: t('shop.topUpDone'),
        text: t('shop.topUpDoneText', { coins: n(result.coins), gems: n(result.gems) }),
        icon: 'gem',
        domain: 'gems',
      });
      return true;
    },
  });

  // La coquille décide de la mise en page (D§7) ; la boutique n'en sait rien.
  const shell = createShell({ initial: 'build' });
  shell.mount('build', shop.el);

  // Section « Ville » : achat de terrain (§6) et cadrage. Les commandes de caméra
  // n'étaient accessibles qu'en `?debug` — donc à personne.
  const citySection = createCitySection({
    /*
      ROADMAP 5.2 — rendre le terrain qu'un réseau devenu inutile confisque.

      Le geste passe par `cleanupOrphanRoads`, qui rappelle `removeRoad` case par case :
      l'invariant de connexité tient donc par construction, et non parce qu'on aurait
      pensé à le revérifier ici.
    */
    onCleanRoads: () => {
      const result = cleanupOrphanRoads(store.getSnapshot(), Date.now());
      if (result.removed.length === 0) {
        sound.play('refused');
        return;
      }
      store.setSnapshot(result.snapshot);
      void renderer.syncRoads(activeIsland(result.snapshot).roads);
      refreshProps();
      sound.play('landExpand', { gain: 0.5 });
      toast.show({
        title: t('city.cleanedRoads'),
        text: t('city.cleanedRoadsText', { count: result.removed.length }),
        icon: 'road',
        domain: 'coins',
      });
    },
    orphanRoads: () => orphanRoadCount(store.getSnapshot(), Date.now()),
    onBuyLand: () => {
      const before = store.getSnapshot();
      const result = purchaseLand(before, Date.now());
      if (!result.ok) {
        sound.play('refused');
        hud.refuse();
        track(EVENTS.actionRefused, { reason: 'land' });
        return;
      }
      // Le premier terrain : le joueur a compris qu'il pouvait s'étendre. Mesuré à
      // l'ACHAT et non au clic, sinon un refus compterait comme une étape franchie.
      if (before.landPurchasesTotal === 0) track(EVENTS.firstLandBought);
      // Le toast d'achat part AVANT l'encaissement : si un jalon de terrain tombe,
      // c'est SON toast — celui qui annonce les gemmes — qui doit rester à l'écran.
      sound.playPlacement();
      toast.show({
        title: t('city.landBought'),
        text: t('city.landCost', { value: n(result.cost) }),
      });
      const applied = applyWithMilestones(result.snapshot);
      renderer.setGridTo(activeIsland(applied).grid);
      // B.5 — la terre TREMBLE quand elle s'agrandit. Un des trois seuls événements
      // qui secouent l'écran ; la production courante n'y a jamais droit.
      renderer.shakeScreen(JUICE.shake.amplitudePx, JUICE.shake.durationMs);
      /*
        La couronne neuve reçoit son DÉCOR tout de suite.

        `setGridTo` reconstruit les tuiles, et rien d'autre : les props ne sont recalculés
        qu'à la pose suivante. Le terrain gagné restait donc un anneau de gazon nu autour
        d'une campagne peuplée — un achat qu'on vient de payer se lisait comme incomplet.
        Le pire moment pour donner cette impression est celui où le joueur vient de
        dépenser.
      */
      refreshProps();
    },
    /*
      G§4 B1 — la MÊME commande que dans la boutique, appelée depuis l'écran où le
      manque de place se constate. Deux chemins vers un seul achat, jamais deux achats :
      dupliquer la logique ici, c'est garantir qu'elle divergera (voir `moveBuilding`).
    */
    onBuyLandWithGems: () => {
      const result = buyLandWithGems(store.getSnapshot(), Date.now());
      if (!result.ok) {
        sound.play('refused');
        hud.refuse();
        toast.show(
          result.refusal === 'too-few-gems'
            ? { title: t('gems.tooFew'), text: t('gems.tooFewHint') }
            : { title: t('city.landMaxed') },
        );
        return;
      }
      sound.playPlacement();
      toast.show({ title: t('gems.landBought') });
      const applied = applyWithMilestones(result.snapshot);
      renderer.setGridTo(activeIsland(applied).grid);
      refreshProps();
    },
    onZoomIn: () => {
      renderer.zoomBy(1.25);
    },
    onZoomOut: () => {
      renderer.zoomBy(0.8);
    },
    onRecenter: () => {
      renderer.recenter();
    },
    /*
      Renommer sa ville, la seule écriture d'identité que le client possède (§5).
      Le retour du serveur fait foi : la section n'affiche que ce qui a été accepté,
      elle ne suppose jamais le succès — hors-ligne, le nom d'avant reste.
    */
    onRename: async (name) => {
      const renamed = await sync.rename(name);
      if (renamed === null) {
        toast.show({ title: t('city.renameFailed'), domain: 'alert' });
        return null;
      }
      sound.play('purchase', { gain: 0.4 });

      /*
        ── LA RÉCOMPENSE, UNE SEULE FOIS ────────────────────────────────────

        Demande de Benoit (22 août) : renommer rapporte des gemmes, et seules les villes
        renommées entrent au classement. La récompense se range dans `claimedMilestones`,
        donc elle est **idempotente par construction** — renommer dix fois ne paie qu'une.

        ⚠️ Elle ne part que si le nom EST choisi. Un joueur qui « renomme » sa ville en
        « Ma ville » ne l'a pas nommée : le classement l'écarterait quand même, et le payer
        pour ça lui apprendrait que le geste ne sert à rien.
      */
      const before = store.getSnapshot();
      const named = !isDefaultCityName(renamed.name, defaultCityNames());
      if (named && !before.claimedMilestones.includes(RENAME_CLAIM_ID)) {
        store.setSnapshot({
          ...before,
          gems: before.gems + RENAME_REWARD_GEMS,
          claimedMilestones: [...before.claimedMilestones, RENAME_CLAIM_ID],
          updatedAt: Date.now(),
        });
        hud.celebrateGems();
        sound.play('milestone');
        toast.show({
          title: t('city.renameRewarded', { value: n(RENAME_REWARD_GEMS) }),
          icon: 'gem',
          domain: 'gems',
        });
      } else {
        toast.show({ title: t('city.renamed'), text: renamed.name });
      }
      return renamed.name;
    },
  });
  shell.mount('city', citySection.el);

  shell.mount('social', socialSection.el);

  /* ── Quêtes du jour ────────────────────────────────────────────────── */

  const questPanel = createQuestPanel({
    onClaim: (questId) => {
      const snapshot = store.getSnapshot();
      const result = claimQuest(snapshot.quests, questCounters(snapshot), questId);
      if (!result.ok) return;

      // Les récompenses sont en GEMMES, jamais en production (§14).
      store.setSnapshot({
        ...snapshot,
        gems: snapshot.gems + result.gems,
        quests: result.state,
      });
      sound.play('milestone');
      hud.celebrateGems();
      toast.show({ title: t('quests.claimed'), text: `+${String(result.gems)} 💎` });
    },
    onClaimWeekly: (questId) => {
      const snapshot = store.getSnapshot();
      const result = claimWeekly(snapshot.weekly, questCounters(snapshot), questId);
      if (!result.ok) return;

      store.setSnapshot({
        ...snapshot,
        gems: snapshot.gems + result.gems,
        weekly: result.state,
      });
      sound.play('milestone');
      hud.celebrateGems();
      toast.show({ title: t('quests.claimed'), text: `+${String(result.gems)} 💎` });
    },
    onClaimStreak: () => {
      const snapshot = store.getSnapshot();
      const result = claimStreak(snapshot.quests);
      if (!result.ok) return;

      store.setSnapshot({
        ...snapshot,
        gems: snapshot.gems + result.gems,
        quests: result.state,
      });
      sound.play('milestone');
      hud.celebrateGems();
      toast.show({ title: t('quests.claimed'), text: `+${String(result.gems)} 💎` });
    },
    /*
      ── RELANCE (G§3, A6) ───────────────────────────────────────────────────

      La sélection du jour est déterministe et NON stockée (`questsForDay`) : la relance
      persiste donc un décalage dans `QuestState.rerolls`. Une relance payée qui se
      perdrait au premier rechargement ne serait pas rachetée une seconde fois.
    */
    onReroll: () => {
      const result = buyQuestReroll(store.getSnapshot(), Date.now());
      if (!result.ok) {
        sound.play('refused');
        hud.refuse();
        if (result.refusal === 'too-few-gems') {
          toast.show({ title: t('gems.tooFew'), text: t('gems.tooFewHint') });
        }
        return;
      }
      store.setSnapshot(result.snapshot);
      sound.play('purchase');
      toast.show({ title: t('gems.rerollBought') });
    },
    /*
      ── RACHAT DE CHAÎNE (G§6, F1) ──────────────────────────────────────────

      Le plafond hebdomadaire vit dans la commande, pas ici : c'est la condition
      d'honnêteté de l'offre, et une règle d'économie ne se garde pas dans un écran.
    */
    onRepairStreak: () => {
      const result = buyStreakRepair(store.getSnapshot(), Date.now());
      if (!result.ok) {
        sound.play('refused');
        hud.refuse();
        toast.show(
          result.refusal === 'too-few-gems'
            ? { title: t('gems.tooFew'), text: t('gems.tooFewHint') }
            : { title: t('gems.repairDone') },
        );
        return;
      }
      store.setSnapshot(result.snapshot);
      sound.play('milestone');
      hud.celebrateGems();
      toast.show({
        title: t('gems.repairBought'),
        text: t('quests.streak', { value: String(result.snapshot.quests.streak) }),
      });
    },
  });

  /*
    ── LE PRESTIGE, monté au-dessus des quêtes ───────────────────────────────

    C'est le manquement n° 1 relevé par l'audit du 17 août : les douze extensions de
    terrain s'achètent en 4,3 h de jeu actif, et **rien n'ouvrait derrière**. Le principe
    premier du §1 l'interdit sans ambiguïté.

    Il est au-dessus des quêtes parce qu'il est l'horizon LOINTAIN : les quêtes disent
    quoi faire aujourd'hui, le prestige dit vers quoi tout cela mène. Et il reste visible
    même indisponible — la distance qui reste EST un objectif, ce que le §1 réclame à
    chaque instant.
  */
  const prestigePanel = createPrestigePanel({
    onPrestige: () => {
      const snapshot = store.getSnapshot();
      const preview = prestigePreview(prestigeProgress(snapshot), snapshot.prestigeMultiplier);
      if (!preview.available) {
        sound.play('refused');
        hud.refuse();
        return;
      }

      // La première ère : le joueur a compris la boucle, et il y revient. C'est le
      // dernier jalon de l'entonnoir d'entrée, et le plus difficile à atteindre.
      if (snapshot.prestigeLevel === 0) track(EVENTS.firstPrestige);

      // On perd sa ville : c'est le geste le plus engageant du jeu, et il annonce son
      // gain DANS la question plutôt que de le faire découvrir après coup.
      prestigeConfirm.ask(t('prestige.confirmText', { value: n(preview.nextMultiplier) }), () => {
        /*
          B.3 — la ville S'ENVOLE avant le reset : « la perte doit être un feu
          d'artifice, pas un écran blanc ». Le son part au décollage ; le reset ne
          s'applique qu'une fois la ville en l'air.

          Le calcul du prestige se fait DANS `onDone`, pas avant l'envol : la
          production continue pendant l'animation, et un reset calculé sur l'état
          d'avant perdrait une seconde de jeu — infime, mais un reset doit être
          exact, pas presque exact.
        */
        sound.play('prestige');

        renderer.playPrestigeFarewell(performance.now(), () => {
          // L'île relue à l'instant du reset, pas celle d'avant l'envol : une pose
          // pendant l'animation aurait sinon laissé un sprite orphelin à l'écran.
          const departing = activeIsland(store.getSnapshot());
          const result = prestige(store.getSnapshot(), Date.now());
          if (!result.ok) return;

          store.setSnapshot(result.snapshot);
          // La ville entière disparaît : les sprites sont oubliés un à un plutôt que
          // resynchronisés, sans quoi `syncBuildings` les retrouverait « déjà présents ».
          for (const building of departing.buildings) renderer.forget(building.id);
          renderer.setGridTo(activeIsland(result.snapshot).grid);
          void renderer.syncBuildings(activeIsland(result.snapshot).buildings, false);
          void renderer.syncRoads(activeIsland(result.snapshot).roads);
          refreshProps();
          renderer.recenter();
          // Les souvenirs des visiteurs repoussent dans l'ère neuve (0.36.0) : les
          // visites sont acquises, leurs trophées renaissent placés pour cette grille.
          spawnTraces();
          /*
            Moment 2 (6.2) — après la fanfare, pas pendant : la célébration appartient au
            joueur, et une boîte qui s'ouvre au milieu la lui vole (E§1.4). Le délai
            couvre l'envol de la ville et la renaissance.
          */
          window.setTimeout(() => {
            offerAccount('era');
          }, 3200);

          // B.5 / E§1.6 — le tremblement du prestige, à l'instant de la renaissance,
          // avec l'amplitude propre au prestige (plus forte que la secousse commune).
          renderer.shakeScreen(JUICE.prestige.shakeAmplitudePx, JUICE.prestige.shakeMs);

          hud.celebrateGems();
          toast.show({
            title: t('prestige.done'),
            text: t('prestige.doneText', { value: n(result.snapshot.prestigeMultiplier) }),
            icon: 'star',
            domain: 'prestige',
            duration: 6000,
          });
        });
      });
    },
  });

  /*
    ── Ce que le joueur a ACCOMPLI, et non seulement ce qui l'attend ──────────

    La Progression ne montrait que des tâches : quêtes du jour, quêtes de la semaine,
    prochaine ère. Soixante-dix jalons franchis et une division gagnée à la sueur
    n'apparaissaient nulle part — la division vivait en petit dans un coin du bandeau,
    les jalons seulement un par un dans le filet du HUD.

    L'ordre de lecture va du long terme au palmarès : la ligue dit où l'on se situe,
    le livre dit ce qu'on a franchi. Tous deux affichent une progression qui ne se
    termine jamais (§1) — l'échelle des divisions est générée, celle des jalons aussi.
  */
  const leagueCard = createLeagueCard();

  const milestoneBook = createMilestoneBook();

  /*
    ── LE CLASSEMENT (§15, ROADMAP 7.1) ─────────────────────────────────────

    L'onglet que le commentaire ci-dessous annonçait comme « n'existant pas encore ». Il
    existe maintenant, parce que la colonne qu'il lit a enfin un écrivain :
    `refresh-ranking` recalcule `total_production` côté serveur, hors de portée du client.

    ⚠️ Il ne se recharge PAS à chaque tick. Le classement bouge à la vitesse de la passe
    serveur — quelques fois par jour — et une liste qui se réécrit sous les yeux du joueur
    lui ferait croire à un direct qui n'existe pas. Il se lit à l'ouverture de l'onglet.
  */
  const rankingPanel = createRankingPanel();

  /*
    ══ PROGRESSION EN ONGLETS (ROADMAP 3.1) ══════════════════════════════════

    Quatre panneaux s'empilaient dans une seule colonne — quêtes, prestige, ligue,
    livre des jalons — soit la section la plus chargée du dock, et de loin. Demande de
    Benoit du 20 août : « fais des onglets aussi dans les autres menus comme dans
    construire ».

    Le découpage est celui du 3.1, moins les deux écrans qui n'existent pas encore
    (Classements et Statistiques, lot 7) : **Quêtes · Jalons · Ères**. La ligue vit sous
    Ères — elle dit où l'on se situe sur le long terme, pas ce qu'on fait aujourd'hui.

    ⚠️ **Le dock reste à quatre entrées** (D§7) : ce sont des onglets DANS une section,
    jamais de nouvelles entrées. Une cinquième entrée coûterait la lisibilité du dock
    pour ranger ce qui tient déjà.
  */
  const progressTabs = createTabbedSection([
    {
      id: 'quests',
      label: t('progress.tabQuests'),
      icon: 'check',
      content: [questPanel.el],
    },
    {
      id: 'milestones',
      label: t('progress.tabMilestones'),
      icon: 'book',
      content: [milestoneBook.el],
    },
    {
      id: 'ranking',
      label: t('progress.tabRanking'),
      icon: 'trophy',
      content: [rankingPanel.el],
      /*
        Le classement se lit À L'OUVERTURE de l'onglet, jamais avant. Le charger au montage
        coûterait trois requêtes à chaque démarrage de partie pour un écran que la plupart
        des joueurs n'ouvriront pas — et le §1 exige d'être jouable en moins de cinq
        secondes, ce qu'on ne tient pas en payant d'avance ce qui peut attendre.
      */
      onShow: () => {
        rankingPanel.refresh(sync.city()?.slug ?? null);
      },
    },
    {
      id: 'eras',
      label: t('progress.tabEras'),
      icon: 'star',
      content: [prestigePanel.el, leagueCard.el],
    },
  ]);
  shell.mount('progress', progressTabs.el);

  // « Mes jeux » — cinquième onglet du dock (demande de Benoit, 18 août 2026) :
  // la vitrine croisée a sa propre section, pas un strapontin sous la Progression.
  const promoSection = createPromoSection();
  shell.mount('games', promoSection.el);

  /*
    Tutoriel d'accueil (demande de Benoit) — la mascotte guide : construire,
    agrandir, s'inscrire, regarder une pub. Non bloquant, congédiable, persistant.
    Sous `?debug` il ne se montre que si `?tutorial` l'accompagne : les sondes
    existantes gardent leur écran, celle du tutoriel demande le sien.
  */
  const tutorialParams = new URLSearchParams(location.search);
  const tutorial =
    !tutorialParams.has('debug') || tutorialParams.has('tutorial')
      ? createTutorial({
          highlight: (section) => {
            shell.pulse(section);
          },
          /*
            ── LE PREMIER GOÛT EST OFFERT (G§7.5) ────────────────────────────

            On ne vend pas ce qu'on n'a jamais fait ressentir. Un joueur qui n'a jamais
            vu sa production doubler ne peut pas décider si 20 gemmes valent la peine :
            il ne compare rien. Le plus petit palier est donc offert une fois, à la fin
            du guide — c'est la mesure la plus honnête du document, et la plus efficace.

            `grantBoost` plutôt que `buyRush` : c'est un cadeau, il ne débite pas. Et il
            passe par l'écrivain unique de `boosts`, donc il se cumule proprement avec
            une pub et se prolonge si le joueur en rachète un.

            Une seule fois par installation : `onCompleted` ne part qu'au franchissement
            de la dernière étape, et la progression du guide est persistée.
          */
          onCompleted: () => {
            // Le premier palier du catalogue — un tuple `readonly`, donc toujours là.
            const tier = GEM_SPENDING.rush[0];
            const now = Date.now();
            store.setSnapshot(
              grantBoost(store.getSnapshot(), 'gems', tier.multiplier, tier.durationMs, now),
            );
            sound.play('milestone');
            hud.celebrateGems();
            toast.show({
              title: t('gems.gift'),
              text: t('gems.giftText', {
                value: n(tier.multiplier),
                duration: duration(tier.durationMs),
              }),
              icon: 'bolt',
              domain: 'gems',
            });
          },
        })
      : null;
  if (tutorial !== null) shopHost.append(tutorial.el);

  // Panneau des traces de visiteurs : vendre ou déplacer (0.36.0).
  const tracePanel = createTracePanel();

  const prestigeConfirm = createConfirm({
    title: t('prestige.confirm'),
    confirmLabel: t('prestige.action'),
    cancelLabel: t('common.cancel'),
    icon: 'star',
  });

  /* ── Actions sur un bâtiment posé ──────────────────────────────────── */

  const buildingPanel = createBuildingPanel({
    onUpgrade: (id) => {
      const result = upgradeBuilding(store.getSnapshot(), id, Date.now());
      if (!result.ok) {
        sound.play('refused');
        hud.refuse();
        return;
      }
      store.setSnapshot(result.snapshot);
      // Le palier change de MODÈLE : il faut recréer le sprite, pas le retoucher.
      renderer.forget(id);
      void renderer.syncBuildings(activeIsland(result.snapshot).buildings, true);
      sound.play('milestone');
      toast.show({ title: t('building.upgraded') });
    },
    /*
      ROADMAP 2.5 — le palier maximum contre des gemmes.

      Aucun prix nouveau : `buyMaxLevel` complète le solde au barème publié de
      `GEM_SPENDING.skip`, consomme un créneau quotidien et ne crédite pas
      `totalProduced`. Le §14 tient parce que ces trois règles vivent dans
      `core/topUp.ts` et sont RÉUTILISÉES, pas recopiées.
    */
    onMaxLevel: (id) => {
      const result = buyMaxLevel(store.getSnapshot(), id, Date.now());
      if (!result.ok) {
        sound.play('refused');
        hud.refuse();
        return;
      }
      store.setSnapshot(result.snapshot);
      // Chaque palier est un MODÈLE différent : on recrée le sprite, on ne le retouche pas.
      renderer.forget(id);
      void renderer.syncBuildings(activeIsland(result.snapshot).buildings, true);
      sound.play('milestone');
      hud.celebrateGems();
      toast.show({
        title: t('building.maxedOut'),
        text: t('building.maxedOutText', { count: result.levels, gems: n(result.gems) }),
        icon: 'gem',
        domain: 'gems',
      });
    },
    onRotate: (id) => {
      store.setSnapshot(rotateBuilding(store.getSnapshot(), id, Date.now()).snapshot);
      renderer.forget(id);
      void renderer.syncBuildings(activeIsland(store.getSnapshot()).buildings, false);
      sound.play('purchase', { gain: 0.4 });
    },
    onMove: (id) => {
      // Le déplacement est GRATUIT (§7) : c'est la composition de sa ville qui crée
      // l'attachement du joueur, donc le partage. Le taxer serait contre-productif.
      const moving = activeIsland(store.getSnapshot()).buildings.find((b) => b.id === id);
      movingId = id;
      canvas.classList.add('is-moving');
      setPlacementMode(true);
      renderer.setFocus(null);
      buildingPanel.close();

      // Fantôme du bâtiment déplacé : sans aperçu, on repose à l'aveugle et on découvre
      // le refus après coup. Le bâtiment d'origine reste visible tant qu'on n'a pas
      // validé — annuler ne doit rien coûter.
      // En déplacement, le fantôme reprend le palier RÉEL du bâtiment déplacé — pas
      // celui qu'aurait un exemplaire neuf.
      if (moving !== undefined) void renderer.setGhost(moving.type, moving.level);
      toast.show({ title: t('building.moveHint') });
    },
    /*
      La vente DEMANDE confirmation — la seule action du jeu qui le mérite.

      D§3 veut des modales rares, et c'est juste : une confirmation sur un geste
      réversible n'est qu'une friction, et un jeu qui en met partout apprend au joueur à
      valider sans lire. Mais vendre détruit le bâtiment, ne rend que la moitié du prix,
      et ne se défait pas. Un seul tap suffisait (audit § U9) — sur un panneau dont les
      quatre boutons sont côte à côte, dont trois sont gratuits et réversibles.

      Le montant est annoncé DANS la question : « es-tu sûr ? » sans chiffre demande au
      joueur de décider sans l'information dont il a besoin.
    */
    onSell: (id) => {
      const amount = sellValue(store.getSnapshot(), id);
      sellConfirm.ask(t('building.sellConfirmText', { value: n(amount) }), () => {
        const result = sellBuilding(store.getSnapshot(), id, Date.now());
        if (!result.ok) return;
        // La rue qui menait au bâtiment vendu ne dessert plus rien : on l'élague, sans
        // quoi la ville se couvre peu à peu d'impasses qui occupent du terrain.
        const tidied = tidyRoads(result.snapshot);
        store.setSnapshot(tidied);
        renderer.forget(id);
        void renderer.syncBuildings(activeIsland(tidied).buildings, false);
        void renderer.syncRoads(activeIsland(tidied).roads);
        refreshProps();
        renderer.setFocus(null);
        buildingPanel.close();
        sound.play('coin');
        toast.show({ title: t('building.sold'), text: `+${n(result.amount ?? 0)}` });
      });
    },
    onClose: () => {
      renderer.setFocus(null);
      buildingPanel.close();
    },
  });
  shopHost.append(buildingPanel.el);
  shopHost.append(shell.el);

  // Une seule boîte, réutilisée : `createModal` pose un écouteur sur le document, et en
  // fabriquer une par vente en accumulerait un par bâtiment vendu.
  const sellConfirm = createConfirm({
    title: t('building.sellConfirm'),
    confirmLabel: t('building.sell'),
    cancelLabel: t('common.cancel'),
    icon: 'coin',
  });

  // Retirer une rue est irréversible au même titre que vendre : le réseau se retrace
  // depuis les poses, il ne se restaure pas.
  const roadConfirm = createConfirm({
    title: t('placement.roadTitle'),
    confirmLabel: t('placement.roadRemove'),
    cancelLabel: t('common.cancel'),
    icon: 'build',
  });

  /*
    ── DÉFRICHAGE (5.3) ──────────────────────────────────────────────────────

    Même composant que le retrait d'une rue, et pour la même raison : ce sont deux gestes
    irréversibles sur le décor, qui coûtent, et qu'on ne doit pas déclencher d'un pouce
    posé au mauvais endroit. Le titre porte le PRIX — une confirmation qui ne dit pas
    combien elle coûte n'est pas une confirmation.
  */
  const clearConfirm = createConfirm({
    title: t('clearing.title'),
    confirmLabel: t('clearing.confirm'),
    cancelLabel: t('common.cancel'),
    icon: 'flower',
  });

  /** Propose de défricher la case, ou de faire repousser ce qui l'a été. */
  function askClear(col: number, row: number): void {
    const snapshot = store.getSnapshot();
    const island = activeIsland(snapshot);
    const key = `${String(col)}:${String(row)}`;

    /*
      Une case DÉJÀ défrichée propose l'inverse : faire repousser. Sans ce retour, un
      joueur qui se ravise n'a aucun recours — et une ville rasée par erreur le reste.
      Rien n'est remboursé, mais le compteur de prix redescend : se raviser ne doit pas
      renchérir les défrichages suivants.
    */
    if (island.cleared.includes(key)) {
      clearConfirm.ask(t('clearing.regrowTitle'), () => {
        store.setSnapshot(regrowCell(store.getSnapshot(), col, row, Date.now()));
        refreshProps();
        sound.playPlacement();
        toast.show({ title: t('clearing.regrown'), icon: 'flower' });
      });
      return;
    }

    const cost = clearCost(island.cleared.length);

    /*
      Trop cher : on OUVRE la boutique au lieu de constater le manque. Même règle que la
      pose d'un bâtiment — « au lieu de m'ouvrir la boutique » (Benoit, 21 août). Un refus
      doit proposer, jamais seulement énoncer.
    */
    if (snapshot.coins < cost) {
      sound.play('refused');
      hud.refuse();
      toast.show({
        title: t('clearing.tooExpensive', { value: n(cost) }),
        text: t('clearing.tooExpensiveHint'),
        icon: 'coin',
        domain: 'coins',
      });
      return;
    }

    clearConfirm.ask(t('clearing.askTitle', { value: n(cost) }), () => {
      const result = clearCell(store.getSnapshot(), col, row, Date.now());
      if (!result.ok) return;
      store.setSnapshot(result.snapshot);
      refreshProps();
      sound.play('coin', { gain: 0.4 });
      toast.show({
        title: t('clearing.done', { value: n(result.spent) }),
        icon: 'flower',
        domain: 'coins',
      });
    });
  }

  /**
   * Applique un snapshot en encaissant les jalons atteints, d'où qu'ils viennent.
   *
   * L'encaissement ne vivait qu'à la pose d'un bâtiment : un jalon de production
   * atteint en regardant la ville produire remplissait la barre du HUD à 100 % et y
   * RESTAIT — aucune gemme, aucun objectif suivant, jusqu'à la pose suivante. Même
   * défaut pour les jalons de terrain à l'achat d'une extension. Une barre pleine qui
   * ne bouge plus est exactement l'état que le principe premier du §1 interdit : tout
   * changement d'état susceptible d'atteindre un jalon passe donc par ici.
   */
  /** Confettis : une fois par session (E§1.4). La 110e maison n'a pas droit à l'orchestre. */
  let confettiPlayed = false;

  /**
   * Sort de terre ce qu'une construction ou une rue vient de recouvrir.
   *
   * Rapport de Benoit (20 août) : une maison posée sur une décoration la rendait
   * injoignable — le tap résout le bâtiment d'abord, donc plus moyen de la vendre ni
   * de la déplacer. `canPlace` interdit désormais la pose ; il restait les RUES, dont
   * le tracé est calculé et traverse ce qu'il faut pour rejoindre le réseau.
   *
   * Appelée sur le chemin commun de toutes les poses, et au démarrage pour les villes
   * déjà abîmées. Sur une ville saine elle rend le MÊME objet — aucun coût, aucune
   * sauvegarde déclenchée.
   */
  function rescueBuried(snapshot: CitySnapshot): CitySnapshot {
    const repair = relocateBuriedTraces(snapshot, footprintCells, Date.now());
    if (repair.snapshot === snapshot) return snapshot;

    void renderer.syncTraces(activeIsland(repair.snapshot).traces, false);
    if (repair.moved > 0) {
      toast.show({
        title: t('traces.rescued'),
        text: t('traces.rescuedMoved', { count: repair.moved }),
        icon: 'flower',
      });
    }
    if (repair.stashed > 0) {
      toast.show({
        title: t('traces.rescued'),
        text: t('traces.rescuedStashed', { count: repair.stashed }),
        icon: 'flower',
      });
    }
    return repair.snapshot;
  }

  function applyWithMilestones(snapshot: CitySnapshot): CitySnapshot {
    const claimed = claimMilestones(rescueBuried(snapshot), Date.now());
    store.setSnapshot(claimed.snapshot);

    if (claimed.claimed.length > 0) {
      const first = claimed.claimed[0];
      if (first !== undefined) {
        sound.play('milestone');
        hud.celebrateGems();
        /*
          B.3 — le jalon SE VOIT : gerbe de particules, et confettis plein écran la
          PREMIÈRE fois de la session seulement (E§1.4, « la rareté fait l'effet »).
          La règle de rareté vit ici, pas dans le renderer : c'est une règle de
          session, et le renderer ne sait pas ce qu'est une session.
        */
        // Féliciter et conseiller en même temps, c'est voler son moment au joueur (E§1.4).
        companion.mute(2600);
        renderer.celebrateMilestone(performance.now(), !confettiPlayed);
        // B.5 — seul le PREMIER jalon de la session secoue : c'est lui le « gros ».
        if (!confettiPlayed) {
          renderer.shakeScreen(JUICE.shake.amplitudePx, JUICE.shake.durationMs);
        }
        confettiPlayed = true;
        toast.show({
          title: t(first.labelKey as TranslationKey, { value: labelFor(first.labelParam) }),
          text: t('milestones.reward', { count: claimed.gems }),
        });
      }
    }

    return claimed.snapshot;
  }

  /**
   * Explique un refus de pose — le motif est calculé, il doit être DIT.
   *
   * `placeBuilding` rend un `refusal` précis, et sa documentation annonce que « le motif
   * rendu pilote le juice (E§1.3) ». Il était intégralement jeté : le jeu jouait un son
   * de refus et n'en disait pas plus, pas même la secousse du compteur que le chemin
   * boutique déclenche pourtant. Le joueur tapait donc une deuxième, une troisième fois,
   * sans jamais savoir si le problème était son solde, sa case, ou son île.
   *
   * Les trois motifs demandent trois réponses différentes, et c'est tout l'intérêt de les
   * distinguer : un manque de pièces ne se corrige pas en tapant ailleurs, une case
   * occupée si, et une île pleine renvoie à l'achat de terrain.
   */
  function explainRefusal(refusal: PlacementResult['refusal'], cost: number): void {
    sound.play('refused');
    hud.refuse();

    /*
      Un refus répété est un défaut de CONCEPTION, pas un joueur maladroit. Le motif est
      donc la seule chose qui compte ici — « plus de place » cinquante fois par jour ne
      dit pas la même chose que « pas assez de pièces ».
    */
    track(EVENTS.actionRefused, { reason: refusal ?? 'unknown' });

    const missing = Math.max(0, cost - store.getSnapshot().coins);

    switch (refusal) {
      case 'too-expensive':
        /*
          ── UN REFUS DOIT PROPOSER, PAS SEULEMENT CONSTATER ────────────────────

          « Ça me dit toujours pas assez de pièces quand je pose un bâtiment, au lieu de
          m'ouvrir la boutique » (Benoit, 21 août). Le jeu annonçait le manque et
          s'arrêtait là — alors qu'il EXISTE un remède, livré au lot 1.3 : compléter le
          solde en gemmes.

          C'est le défaut n° 3 du G§1, celui que la boutique de gemmes traite déjà pour les
          décorations : « l'offre ne doit JAMAIS être muette au moment précis où le joueur
          veut payer ». Un manque de pièces est exactement ce moment-là.

          ⚠️ On ouvre l'onglet « Dépenser », pas « Obtenir » : ce qui manque est des
          PIÈCES, et le remède est l'avance de production — pas un pack de gemmes. Mener
          vers l'achat quand la réponse est déjà dans son solde serait une vente forcée.
        */
        toast.show({
          title: t('placement.tooExpensive'),
          text: t('placement.tooExpensiveHint', { value: n(missing) }),
          icon: 'coin',
          domain: 'coins',
        });
        track(EVENTS.storeOpened, { reason: 'too-expensive' });
        store_.open('spend');
        return;
      case 'cell-occupied':
        toast.show({
          title: t('placement.occupied'),
          text: t('placement.occupiedHint'),
          icon: 'build',
          domain: 'alert',
        });
        return;
      case 'no-space':
        // Le seul refus qui appelle une ACTION : on nomme l'onglet où elle se trouve.
        toast.show({
          title: t('placement.noSpace'),
          text: t('placement.noSpaceHint'),
          icon: 'city',
          domain: 'alert',
        });
        return;
      case 'no-access':
        /*
          Aucune rue ne peut atteindre cet endroit — ou l'y amener couperait la ville du
          reste de son île. Le joueur n'a aucun moyen de deviner cette règle depuis un
          simple son de refus : on la lui dit, en termes de ville et non de graphe.
        */
        toast.show({
          title: t('placement.noAccess'),
          text: t('placement.noAccessHint'),
          icon: 'build',
          domain: 'alert',
        });
        return;
      case 'too-few-gems':
        /*
          G§1, défaut n° 3 : l'offre ne doit JAMAIS être muette au moment précis où le
          joueur veut payer. On MÈNE à la boutique au lieu de refuser — même geste que
          pour une décoration trop chère (2.1).

          ⚠️ Mais on le DIT d'abord. Ouvrir une boutique sans un mot, c'est ce que Benoit a
          décrit le 21 août : « quand j'essaye de le poser sur la carte, ça me dit que j'ai
          pas de gemmes, c'est tout ». La boutique répondait à une question qu'il n'avait
          pas posée. Ce chemin ne devrait plus être atteint depuis la carte — le refus
          arrive maintenant au clic — mais il reste pour la pose au fantôme.
        */
        toast.show({
          title: t('shop.shortOnGems', {
            value: n(Math.max(0, Math.ceil(cost - store.getSnapshot().gems))),
          }),
          icon: 'gem',
          domain: 'gems',
        });
        track(EVENTS.storeOpened, { reason: 'too-few-gems' });
        store_.open('buy');
        return;
      case 'already-owned':
        toast.show({
          title: t('placement.alreadyOwned'),
          text: t('placement.alreadyOwnedHint'),
          icon: 'lmTower',
          domain: 'gems',
        });
        return;
      default:
        // `locked` et `unknown-type` ne sont pas atteignables par le chemin joueur : la
        // boutique refuse le clic en amont. Pas de toast pour un état impossible.
        return;
    }
  }

  /**
   * Pose plusieurs exemplaires d'affilée, en un seul geste.
   *
   * ── Pourquoi ce n'est pas une simple boucle sur `commitPlacement` ───────────
   *
   * Chaque pose déclenche son juice : un sprite qui tombe, un son, un toast, un tracé de
   * rue animé, un rendu complet du décor. Dix poses ainsi enchaînées donnent dix chutes
   * simultanées, dix sons superposés et dix passes de décor — un vacarme, et des fps
   * perdus au pire moment (E§3 : le juice ne coûte jamais de fps).
   *
   * On boucle donc sur la COMMANDE PURE, et on ne rend qu'une fois à la fin. Le joueur
   * voit un quartier apparaître d'un coup, ce qui est exactement ce qu'il a demandé — et
   * il entend un seul son, pas dix.
   *
   * ── Le nombre se recalcule à chaque tour, il ne se pré-calcule pas ──────────
   *
   * `maxAffordable` répond pour un solde donné ; or le prix monte à chaque exemplaire
   * acheté. Prendre son résultat comme un compte fixe ferait échouer les dernières poses,
   * et la boucle s'arrêterait de toute façon au premier refus. On l'utilise donc comme
   * une BORNE de sécurité, pas comme une vérité — c'est la commande qui tranche, une fois
   * de plus.
   */
  function commitMany(type: string, quantity: number): void {
    /*
      Un point de repère est unique par ère : « ×10 » n'existe pas pour lui, et la barre
      d'action masque d'ailleurs le sélecteur. Le bouton « Poser » reste utile — c'est le
      placement automatique, pour qui ne veut pas choisir sa case.

      ⚠️ Sans cette branche, le bouton ne faisait RIEN : `BUILDINGS.find` ne connaît pas
      les repères, et la fonction sortait en silence. Un bouton muet est exactement ce que
      le G§1 reproche à la boutique.
    */
    if (landmarkByType(type) !== undefined) {
      commitPlacement(type, 'auto');
      return;
    }

    const definition = BUILDINGS.find((entry) => entry.type === type);
    if (definition === undefined) return;

    const snapshot = store.getSnapshot();
    const owned = buildingCounts(snapshot)[type] ?? 0;
    const affordable = maxAffordable(definition, owned, snapshot.coins);
    // 0 signifie MAX. Le plafond dur évite qu'une fortune d'endgame ne lance mille
    // recherches de placement, dont chacune balaie la grille entière.
    const wanted = quantity === 0 ? Math.min(affordable, 250) : quantity;

    if (wanted <= 1) {
      commitPlacement(type, 'auto');
      return;
    }

    let current = snapshot;
    let placed = 0;
    let lastRefusal: PlacementResult['refusal'];
    let lastCost = 0;

    for (let i = 0; i < wanted; i++) {
      const result = placeBuilding(current, type, 'auto', Date.now() + i);
      if (!result.ok) {
        lastRefusal = result.refusal;
        lastCost = result.cost;
        break;
      }
      current = result.snapshot;
      placed++;
      if (result.building !== undefined) renderer.rememberPlacement(result.building);
    }

    // Aucune pose : c'est un refus ordinaire, et il se dit comme tel.
    if (placed === 0) {
      explainRefusal(lastRefusal, lastCost);
      return;
    }

    const applied = applyWithMilestones(current);
    const island = activeIsland(applied);
    void renderer.syncBuildings(island.buildings, true);
    void renderer.syncRoads(island.roads);
    refreshProps();
    sound.playPlacement();

    // On dit combien ont été posés, parce que ce n'est pas toujours ce qui était demandé :
    // le terrain ou le solde peuvent avoir arrêté la série plus tôt.
    toast.show({
      title: t('shop.placedMany', { count: placed }),
      icon: 'build',
      domain: 'coins',
    });

    shell.collapse();
    exitPlacement();
  }

  function commitPlacement(type: string, cell: { col: number; row: number } | 'auto'): void {
    const snapshot = store.getSnapshot();
    const result = placeBuilding(snapshot, type, cell, Date.now());

    /*
      LE premier bâtiment, émis une seule fois. « Premier » veut dire premier : on lit le
      compteur AVANT la pose plutôt que de tenir un drapeau à part, qui se
      désynchroniserait d'une sauvegarde à l'autre.
    */
    if (result.ok && snapshot.buildingsPlaced === 0) {
      track(EVENTS.firstBuildingPlaced, { type });
    }

    if (!result.ok) {
      explainRefusal(result.refusal, result.cost);
      return;
    }

    if (result.building !== undefined) {
      renderer.rememberPlacement(result.building);

      // La route se déroule vers le réseau, avec son propre son : un moment gratuit
      // de satisfaction qu'un tracé instantané dilapiderait.
      const traced = (result.roadPath ?? []).map(
        (cell) => `${String(cell.col)}:${String(cell.row)}`,
      );
      void renderer.syncRoads(activeIsland(result.snapshot).roads, traced);
      // `landExpand` : le son du terrain qui s'ouvre, exactement ce que fait une route.
      if (traced.length > 0) sound.play('landExpand', { gain: 0.5 });
    }

    const applied = applyWithMilestones(result.snapshot);
    void renderer.syncBuildings(activeIsland(applied).buildings);
    /*
      ⚠️ Le décor se rafraîchit APRÈS la pose dans le store, jamais avant.

      `refreshProps` lit `store.getSnapshot()`. Appelé avant `applyWithMilestones`,
      il calculait les cases occupées SANS le bâtiment qu'on venait de poser : le
      lampadaire ou le banc sous l'emprise survivait, planté dans le mur (rapport de
      Benoit, 18 août). Le chemin « en lot » avait déjà le bon ordre — c'est lui le
      modèle.
    */
    if (result.building !== undefined) refreshProps();

    // Après une pose, le joueur doit VOIR ce qu'il vient de construire. En desktop le
    // panneau est à côté de la ville : il n'y a rien à escamoter, et `collapse()` ne
    // fait rien — c'est la coquille qui le sait, pas la boutique.
    shell.collapse();
    // Une seule sortie du mode placement, partagée avec Échap et le tap hors grille :
    // trois copies du même nettoyage finissent toujours par diverger d'une ligne.
    exitPlacement();
  }

  /**
   * Palier visuel qu'aurait un exemplaire neuf de ce type, ici et maintenant.
   *
   * Exactement la règle appliquée par `placeBuilding` — même fonction pure, même
   * source — pour que l'aperçu ne puisse pas diverger de la pose.
   */
  /**
   * Multiplicateur total appliqué à la production, toutes sources confondues.
   *
   * Deux origines, et elles se MULTIPLIENT :
   *
   * · l'accélération achetée en gemmes (§14), ponctuelle et locale ;
   * · les boosts de visite (§9), qui viennent du serveur et qu'aucun joueur ne peut se
   *   fabriquer — c'est ce qui les rend crédibles comme récompense du partage.
   *
   * Le produit, pas la somme : « +100 % » appliqué deux fois donne ×4. Les plafonds font
   * le travail de bornage (dix boosts actifs, une seule accélération à la fois), pas une
   * formule ad hoc qui finirait par ne plus vouloir rien dire.
   */
  function totalBoost(snapshot: CitySnapshot): number {
    return boostMultiplier(snapshot, Date.now()) * visitBoost;
  }

  function ghostLevelFor(type: string): number {
    if (type === '') return 1;
    return NEW_BUILDING_LEVEL;
  }

  function labelFor(param: string | number | undefined): string {
    if (param === undefined) return '';
    if (typeof param === 'number') return n(param);
    // Un type de bâtiment se traduit ; tout le reste est déjà un nombre.
    return t(`buildings.${param}` as TranslationKey);
  }

  /* ── Gestes sur la grille ──────────────────────────────────────────── */

  /**
   * Déplacement maximal d'un doigt, en pixels CSS, pour qu'un geste reste un TAP.
   *
   * ── Le défaut que ce seuil supprime ───────────────────────────────────────
   *
   * `game.ts` écoutait `pointerup` sur le canvas sans rien regarder d'autre, alors que
   * `render/camera.ts` écoute le MÊME événement sur le MÊME élément pour piloter le
   * déplacement de la vue. **Faire glisser la caméra se terminait donc par une pose de
   * bâtiment** — mesuré : un glissement de 120 px posait une maison — ou par l'ouverture
   * du panneau de ce qui se trouvait sous le doigt au relâchement.
   *
   * C'était le geste le plus fréquent du jeu déclenchant l'action la plus coûteuse, et
   * aucun des 371 tests ne pouvait le voir : chaque fonction impliquée est juste, c'est
   * leur cohabitation sur un même événement qui ne l'était pas.
   *
   * ── Pourquoi 10 px, et pourquoi en pixels CSS ─────────────────────────────
   *
   * C'est la valeur qu'emploient les navigateurs pour distinguer un tap d'un glissement
   * (seuil de `click` après `pointerdown`). En dessous, un doigt qui tremble annule des
   * taps légitimes ; au-dessus, un micro-glissement pose encore. En pixels CSS et non
   * physiques : sur un téléphone à DPR 3, un seuil en pixels physiques serait trois fois
   * trop permissif.
   */
  const TAP_SLOP = 10;

  /**
   * Ce que le doigt courant a fait depuis son appui.
   *
   * `null` dès qu'un second pointeur entre en scène : un pincement produit deux
   * `pointerup`, donc deux tentatives de pose, chacune à un endroit différent. Un geste
   * à deux doigts n'est jamais un tap — et il ne le redevient pas quand le second doigt
   * se lève.
   */
  let tap: { readonly id: number; readonly x: number; readonly y: number } | null = null;

  /**
   * Doigts actuellement posés.
   *
   * Le fantôme ne suit RIEN tant qu'il y en a plus d'un : pendant un pincement, les
   * deux doigts émettent des `pointermove`, et le bâtiment sautait de l'un à l'autre au
   * rythme des événements. Le zoom reste disponible pendant la pose — c'est précisément
   * ce dont on a besoin pour viser — mais il ne déplace plus l'aperçu.
   */
  const activePointers = new Set<number>();

  /**
   * Durée d'appui, en millisecondes, pour ATTRAPER un bâtiment déjà posé.
   *
   * ── Pourquoi un appui long, et pourquoi 400 ms ────────────────────────────
   *
   * Déplacer un bâtiment demandait trois gestes : taper dessus, ouvrir le panneau,
   * choisir « Déplacer ». Or c'est le même geste physique que poser — on attrape et on
   * promène — et le faire passer par un menu casse la composition, qui est justement ce
   * qui attache un joueur à sa ville.
   *
   * 400 ms est la convention des systèmes tactiles (Android tient 500 ms, iOS 400 pour
   * l'aperçu). En dessous, un tap un peu appuyé arrache un bâtiment par erreur ; au
   * delà, on croit que rien ne se passe. Le geste s'annule dès que le doigt dépasse le
   * seuil de tap : un déplacement de vue reste un déplacement de vue.
   */
  const LONG_PRESS_MS = 400;
  let longPressTimer = 0;

  /** Annule un appui long en attente. Appelé par tous les chemins qui closent le geste. */
  const cancelLongPress = (): void => {
    globalThis.clearTimeout(longPressTimer);
    longPressTimer = 0;
  };

  const onPointerDown = (event: PointerEvent): void => {
    activePointers.add(event.pointerId);
    // Un second appui invalide le geste en cours et ne s'y substitue pas.
    tap = tap === null ? { id: event.pointerId, x: event.clientX, y: event.clientY } : null;

    cancelLongPress();
    if (
      tap !== null &&
      movingId === null &&
      placingDecor === null &&
      renderer.getGhostType() === null
    ) {
      const start = { x: event.clientX, y: event.clientY };
      longPressTimer = window.setTimeout(() => {
        longPressTimer = 0;
        // Le doigt s'est-il levé ou multiplié entre-temps ? `tap` répond aux deux.
        if (tap?.id !== event.pointerId) return;
        grabAt(start.x, start.y);
      }, LONG_PRESS_MS);
    }

    /*
      ══ LE FANTÔME SAUTE SOUS LE DOIGT DÈS L'APPUI ═════════════════════════════

      C'est ce qui manquait pour que la pose existe sur téléphone.

      Le fantôme ne se déplaçait qu'au `pointermove`, ce qui suppose un SURVOL — un
      geste que le tactile n'a pas. Sur mobile, le joueur choisissait donc un bâtiment
      dans la boutique, ne voyait **rien**, tapait une case, et découvrait le résultat
      après coup. L'aperçu existait et n'était visible que sur ordinateur.

      Depuis cet appel, le bâtiment apparaît sous le doigt à l'instant du contact, suit
      le glissement, et se pose au relâchement. C'est le geste de Clash of Clans, et
      c'est le geste que le joueur répétera des milliers de fois.
    */
    if (tap !== null && (renderer.getGhostType() !== null || placingDecor !== null)) {
      onPointerMove(event);
    }
  };

  const onPointerCancel = (event: PointerEvent): void => {
    activePointers.delete(event.pointerId);
    cancelLongPress();
    tap = null;
  };

  /**
   * Attrape le bâtiment situé sous un point d'écran et entre en mode déplacement.
   *
   * Le geste se poursuit sans rupture : le doigt est déjà posé, le fantôme apparaît
   * sous lui, et le relâchement repose le bâtiment. C'est ce qui distingue « attraper »
   * de « sélectionner puis déplacer ».
   */
  function grabAt(clientX: number, clientY: number): void {
    const rect = canvas.getBoundingClientRect();
    const cell = renderer.cellAtScreen(clientX - rect.left, clientY - rect.top);
    if (cell === null) return;

    const island = activeIsland(store.getSnapshot());
    // L'emprise ENTIÈRE, comme la sélection : attraper une épicerie 2 × 2 par n'importe
    // laquelle de ses quatre cases.
    const picked = island.buildings.find((b) => covers(b, cell.col, cell.row));
    if (picked === undefined) return;

    movingId = picked.id;
    canvas.classList.add('is-moving');
    setPlacementMode(true);
    renderer.setFocus(null);
    buildingPanel.close();
    void renderer.setGhost(picked.type, picked.level);
    // Un retour immédiat, sans texte : le joueur doit SENTIR que le bâtiment est dans
    // sa main avant même de l'avoir bougé.
    sound.play('purchase', { gain: 0.35 });
    // `vibrate` n'existe pas sur iOS ni sur ordinateur : l'absence est la norme, pas
    // une exception à signaler.
    if (typeof globalThis.navigator.vibrate === 'function') globalThis.navigator.vibrate(12);
    // Le fantôme se cale tout de suite sous le doigt : sans cet appel il n'apparaîtrait
    // qu'au premier mouvement, et l'appui long semblerait n'avoir rien fait.
    renderer.moveGhostTo(cell, canPlace(island, picked.type, cell.col, cell.row, picked.id));
  }

  /**
   * Clôt le geste en cours et dit ce qu'il était.
   *
   * ── Pourquoi DEUX notions et non plus une seule ───────────────────────────
   *
   * `isTap()` mélangeait deux questions que le glisser-déposer a séparées :
   *
   * - **`single`** — le geste s'est-il fait d'un seul doigt, du début à la fin ? Un
   *   pincement produit deux `pointerup`, donc deux poses à deux endroits différents.
   *   Cette réponse-là vaut pour tout le monde.
   * - **`moved`** — le doigt a-t-il dépassé le seuil de tap ? Hors mode pose, un
   *   glissement est un déplacement de caméra et ne doit rien sélectionner. **En mode
   *   pose, il est au contraire le geste normal** : on promène le bâtiment et on
   *   relâche. Refuser un geste « trop long » y interdirait précisément le
   *   glisser-déposer.
   */
  const endGesture = (event: PointerEvent): { single: boolean; moved: boolean } => {
    const start = tap;
    tap = null;
    if (start?.id !== event.pointerId) return { single: false, moved: false };
    const distance = Math.hypot(event.clientX - start.x, event.clientY - start.y);
    return { single: true, moved: distance > TAP_SLOP };
  };

  const onPointerMove = (event: PointerEvent): void => {
    // Un doigt qui part est en train de déplacer la vue, pas d'attraper.
    if (longPressTimer !== 0 && tap !== null && tap.id === event.pointerId) {
      const distance = Math.hypot(event.clientX - tap.x, event.clientY - tap.y);
      if (distance > TAP_SLOP) cancelLongPress();
    }

    // Pendant un pincement, le fantôme reste où il est (voir `activePointers`).
    if (activePointers.size > 1) return;

    // Pose d'une décoration : une seule case, aucune contrainte d'adjacence — la
    // règle de validité est donc l'occupation, rien d'autre.
    if (placingDecor !== null) {
      const rect = canvas.getBoundingClientRect();
      const cell = renderer.cellAtScreen(event.clientX - rect.left, event.clientY - rect.top);
      const free =
        cell !== null && !occupiedCellsNow().has(`${String(cell.col)}:${String(cell.row)}`);
      renderer.moveDecorGhostTo(cell, free);
      return;
    }

    const type = renderer.getGhostType();
    if (type === null) return;

    const rect = canvas.getBoundingClientRect();
    const cell = renderer.cellAtScreen(event.clientX - rect.left, event.clientY - rect.top);
    const island = activeIsland(store.getSnapshot());
    // Le fantôme juge sur l'EMPRISE entière, comme le fera le placement. Une seule
    // case testée montrait un fantôme vert là où la pose allait être refusée — ou
    // pire, acceptée par-dessus des maisons.
    // En déplacement, le bâtiment ne doit pas se déclarer en conflit avec lui-même.
    const free =
      cell !== null &&
      (movingId === null
        ? canPlace(island, type, cell.col, cell.row)
        : canPlace(island, type, cell.col, cell.row, movingId));

    renderer.moveGhostTo(cell, free);
  };

  /** Bâtiment en cours de déplacement, le cas échéant. */
  let movingId: string | null = null;

  /** Objet du sac à dos en cours de pose (2.9a), le cas échéant. */
  let placingDecor: { itemId: string; kind: string } | null = null;

  /**
   * Cases occupées à l'instant T — bâtiments (emprise entière), routes, traces,
   * monuments. Le cœur ne connaît pas l'emprise des bâtiments, qui vit dans le rendu :
   * même contrat que `spawnVisitorTraces`. Recalculé à chaque geste plutôt que tenu à
   * jour — une liste entretenue finit toujours par oublier un écrivain.
   */
  function occupiedCellsNow(): Set<string> {
    const island = activeIsland(store.getSnapshot());
    const occupied = new Set<string>(island.roads);
    for (const building of island.buildings) {
      for (const cell of footprintCells(building.type, building.col, building.row)) {
        occupied.add(`${String(cell.col)}:${String(cell.row)}`);
      }
    }
    for (const trace of island.traces) {
      occupied.add(`${String(trace.col)}:${String(trace.row)}`);
    }
    for (const monument of island.monuments) {
      occupied.add(`${String(monument.col)}:${String(monument.row)}`);
    }
    return occupied;
  }

  /**
   * Arme la pose d'une décoration du sac à dos — le fantôme de visée des bâtiments,
   * pour un objet d'une case. L'objet est DÉJÀ payé et en sécurité au sac : chaque
   * sortie de ce mode (pose, Échap, tap hors grille) le laisse ou le consomme, aucune
   * ne le perd.
   */
  function startDecorPlacement(itemId: string): void {
    const item = store.getSnapshot().backpack.find((entry) => entry.id === itemId);
    if (item === undefined) return;
    const definition = decorationById(item.decorationId);
    if (definition === undefined) {
      // Entrée de catalogue inconnue (sauvegarde d'une version future) : l'objet
      // reste au sac, on le dit plutôt que d'armer un fantôme sans texture.
      toast.show({ title: t('decorRefused.unknown') });
      return;
    }

    placingDecor = { itemId, kind: definition.sprite };
    shopHost.classList.add('is-placing');
    setPlacementMode(true);
    renderer.setFocus(null);
    buildingPanel.close();

    // Un doigt DÉJÀ posé est honoré quand le fantôme arrive — même règle que les
    // bâtiments : la texture se charge, l'appui ne doit pas tomber dans le vide.
    void renderer.setDecorGhost(definition.sprite).then(() => {
      if (tap !== null && placingDecor !== null) {
        const rect = canvas.getBoundingClientRect();
        const cell = renderer.cellAtScreen(tap.x - rect.left, tap.y - rect.top);
        const free =
          cell !== null && !occupiedCellsNow().has(`${String(cell.col)}:${String(cell.row)}`);
        renderer.moveDecorGhostTo(cell, free);
      }
    });
    toast.show({ title: t('placement.decorHint'), icon: 'flower' });
  }

  /**
   * Sort du mode pose ou du mode déplacement, quelle qu'en soit la raison.
   *
   * Il n'existait AUCUNE sortie fiable : un relâchement hors de la grille quittait
   * `onPointerUp` par un `return` anticipé sans rien annuler, et le joueur restait piégé
   * en mode placement — chaque tap suivant tentait une pose au lieu de sélectionner.
   * Une seule fonction, appelée par les quatre chemins de sortie : Échap, tap hors
   * grille, pose réussie, déplacement terminé.
   */
  function exitPlacement(notify = false): void {
    const wasActive =
      movingId !== null || renderer.getGhostType() !== null || placingDecor !== null;
    // Annuler la pose d'une décoration ne perd RIEN : l'objet est resté au sac depuis
    // l'achat. Le toast le dit — un joueur qui vient de payer doit savoir où c'est.
    const stashed = placingDecor !== null;
    movingId = null;
    placingDecor = null;
    canvas.classList.remove('is-moving');
    shopHost.classList.remove('is-placing');
    void renderer.setGhost(null);
    void renderer.setDecorGhost(null);
    setPlacementMode(false);
    shop.clearSelection();
    if (wasActive && notify) {
      toast.show(
        stashed
          ? { title: t('placement.decorStored'), icon: 'flower' }
          : { title: t('placement.cancelled') },
      );
    }
  }

  /**
   * Bascule tout ce qui distingue le mode pose du reste du jeu.
   *
   * Deux effets, et un seul endroit pour les deux : le quadrillage apparaît, et **le
   * doigt cesse d'appartenir à la vue pour appartenir au bâtiment**. Les séparer, c'est
   * garantir qu'un jour l'un des deux restera allumé après une sortie — et un joueur
   * qui ne peut plus déplacer sa caméra ne comprendra jamais pourquoi.
   */
  function setPlacementMode(active: boolean): void {
    renderer.setPlacementGrid(active);
    renderer.setDragToPan(!active);
  }

  /**
   * Échap annule le geste en cours.
   *
   * Sur ordinateur c'est le réflexe universel, et c'est la seule sortie possible quand le
   * curseur est déjà hors de la grille. Posé sur le document et non sur le canvas : ce
   * dernier ne reçoit pas le focus clavier, il n'aurait donc jamais vu la touche.
   */
  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape') return;
    if (movingId === null && renderer.getGhostType() === null && placingDecor === null) return;
    exitPlacement(true);
  };

  const onPointerUp = (event: PointerEvent): void => {
    activePointers.delete(event.pointerId);
    cancelLongPress();
    const gesture = endGesture(event);
    const placing = movingId !== null || renderer.getGhostType() !== null || placingDecor !== null;

    // Un pincement n'est jamais une pose, quel que soit le mode. Hors mode pose, un
    // glissement est un déplacement de caméra et ne sélectionne rien ; en mode pose,
    // c'est le glisser-déposer lui-même, et il conclut.
    if (!gesture.single) return;
    if (gesture.moved && !placing) return;

    const rect = canvas.getBoundingClientRect();
    const cell = renderer.cellAtScreen(event.clientX - rect.left, event.clientY - rect.top);

    // Hors grille : c'est une annulation, pas un non-événement. La version précédente
    // sortait par un `return` sec et laissait le joueur en mode placement.
    if (cell === null) {
      if (placing) exitPlacement(true);
      else renderer.setFocus(null);
      buildingPanel.close();
      return;
    }

    // Mode POSE D'UNE DÉCORATION (2.9a) : testé avant tout le reste, comme le
    // déplacement — un fantôme actif change le sens du tap.
    if (placingDecor !== null) {
      const result = placeDecoration(
        store.getSnapshot(),
        placingDecor.itemId,
        cell.col,
        cell.row,
        occupiedCellsNow(),
        Date.now(),
      );
      if (result.ok) {
        store.setSnapshot(result.snapshot);
        sound.playPlacement();
        // `true` : la décoration APPARAÎT — même fanfare qu'une trace qui vient de
        // naître. Ce qu'on vient de payer se voit arriver.
        void renderer.syncTraces(activeIsland(result.snapshot).traces, true);
        refreshProps();
        exitPlacement();
        toast.show({ title: t('shop.decorPlaced'), icon: 'flower', domain: 'gems' });
      } else {
        // Un refus ne quitte PAS le mode et ne consomme rien : le joueur vise ailleurs,
        // l'objet reste au sac. Même règle que le déplacement d'un bâtiment.
        sound.play('refused');
        hud.refuse();
        toast.show({ title: t('placement.occupied'), text: t('placement.occupiedHint') });
      }
      return;
    }

    const type = renderer.getGhostType();

    const island = activeIsland(store.getSnapshot());

    // Mode DÉPLACEMENT : le clic repose le bâtiment sélectionné. Testé AVANT la pose,
    // le fantôme étant actif dans les deux cas.
    if (movingId !== null) {
      // Une SEULE validation, celle de la commande : pré-juger ici avec `canPlace`
      // dupliquait la règle, et les deux copies ont fini par diverger (audit P0.3).
      const result = moveBuilding(store.getSnapshot(), movingId, cell, Date.now());
      if (result.ok) {
        // L'élagage vient APRÈS le raccordement : la rue d'origine ne se retire qu'une
        // fois la nouvelle tracée, sinon on couperait celle qu'on est en train de
        // rejoindre.
        const moved = rescueBuried(tidyRoads(result.snapshot));
        store.setSnapshot(moved);
        sound.playPlacement();
        void renderer.syncBuildings(activeIsland(moved).buildings, true);
        // Le déplacement TRACE une rue, comme la pose : elle se déroule de la même
        // façon. Sans ce passage, le nouveau segment apparaissait d'un coup.
        const traced = (result.roadPath ?? []).map(
          (road) => `${String(road.col)}:${String(road.row)}`,
        );
        void renderer.syncRoads(activeIsland(moved).roads, traced);
        if (traced.length > 0) sound.play('landExpand', { gain: 0.5 });
        refreshProps();
        exitPlacement();
      } else {
        // Un refus ne quitte PAS le mode : le joueur voulait déplacer, il vise ailleurs.
        // Sortir du mode à chaque case ratée obligerait à rouvrir le panneau à chaque
        // essai — le contraire de ce qu'un geste de composition demande.
        sound.play('refused');
        hud.refuse();
        const noAccess = result.refusal === 'no-access';
        toast.show({
          title: noAccess ? t('placement.noAccess') : t('placement.occupied'),
          text: noAccess ? t('placement.noAccessHint') : t('placement.occupiedHint'),
        });
      }
      return;
    }

    // Mode POSE : le fantôme est actif, le clic pose le bâtiment.
    if (type !== null) {
      commitPlacement(type, cell);
      return;
    }

    /*
      Sinon : SÉLECTION. On cherche le bâtiment sous le curseur, emprise comprise —
      cliquer sur n'importe quelle case d'une épicerie 2 × 2 doit la sélectionner, pas
      seulement sa case d'ancrage.
    */
    // Mode DÉPLACEMENT d'une trace : le tap est la destination (0.36.0).
    if (movingTraceId !== null) {
      const moved = moveVisitorTrace(
        store.getSnapshot(),
        movingTraceId,
        cell.col,
        cell.row,
        footprintCells,
        Date.now(),
      );
      if (moved.ok) {
        movingTraceId = null;
        store.setSnapshot(moved.snapshot);
        void renderer.syncTraces(activeIsland(moved.snapshot).traces, false);
        refreshProps();
        sound.playPlacement();
      } else {
        sound.play('refused');
        toast.show({ title: t('placement.occupied'), text: t('placement.occupiedHint') });
      }
      return;
    }

    const picked = island.buildings.find((b) => covers(b, cell.col, cell.row));
    if (picked === undefined) {
      renderer.setFocus(null);
      buildingPanel.close();
      // Taper une TRACE de visiteur ouvre son panneau : vendre ou déplacer (0.36.0).
      const traceId = island.traces.find(
        (trace) => trace.col === cell.col && trace.row === cell.row,
      )?.id;
      if (traceId !== undefined) {
        openTracePanel(traceId);
        return;
      }
      // Taper une RUE propose de la retirer. Sans ce geste, une case de bitume gelait
      // son terrain pour toujours : `canPlace` refuse de bâtir dessus, et l'élagage
      // automatique la garde dès qu'elle borde un bâtiment.
      if (island.roads.includes(`${String(cell.col)}:${String(cell.row)}`)) {
        askRemoveRoad(cell.col, cell.row);
        return;
      }
      /*
        5.3 — taper une DÉCORATION D'AMBIANCE propose de la défricher. C'est le dernier
        cas du tap, et il était vide : une case sans bâtiment, sans trace et sans route ne
        faisait rien du tout, y compris quand un arbre s'y trouvait.

        ⚠️ C'est le RENDU qui sait si un décor est là — il est le seul à connaître le bruit
        qui le place, et `src/core` n'a pas le droit de l'importer. On lui demande donc, au
        lieu de recalculer la règle ici : deux copies du même bruit divergeraient au premier
        réglage de densité.

        Une case déjà défrichée reste tapable : c'est ce qui permet de faire repousser.
      */
      const key = `${String(cell.col)}:${String(cell.row)}`;
      if (renderer.propNameAt(cell.col, cell.row) !== null || island.cleared.includes(key)) {
        askClear(cell.col, cell.row);
      }
      return;
    }

    /*
      Ce qui masque le bâtiment sélectionné devient transparent (ROADMAP 1.8). Le
      renderer décide QUI masque — il est le seul à connaître les bornes à l'écran ; le
      jeu se contente de dire sur quoi le joueur travaille.
    */
    renderer.setFocus(picked.id);
    buildingPanel.open(store.getSnapshot(), picked.id);
  };

  /* ── Traces des visiteurs : vendre, déplacer (0.36.0, demande de Benoit) ── */

  let movingTraceId: string | null = null;

  /**
   * Le panneau d'une trace : une confirmation à deux issues. « Vendre » affiche son
   * montant DANS le bouton — le prix est l'information de la décision ; « Déplacer »
   * arme le tap suivant comme destination.
   */
  function openTracePanel(traceId: string): void {
    const production = currentProduction(store.getSnapshot(), totalBoost(store.getSnapshot()));
    const trace = activeIsland(store.getSnapshot()).traces.find((entry) => entry.id === traceId);
    if (trace === undefined) return;
    /*
      Deux barèmes selon l'ORIGINE (2.9d) : une décoration payée se rembourse au prorata
      de son prix en gemmes, une trace de visiteur vaut des secondes de production. Un
      montant nul (sorte hors catalogue) retire le bouton plutôt que de le laisser
      échouer en silence.
    */
    const value =
      trace.bought === true
        ? boughtDecorationSellValue(trace.kind)
        : traceSellValue(trace.kind, production);

    tracePanel.ask(value <= 0 ? null : t('traces.sellFor', { value: n(value) }), (choice) => {
      if (choice === 'sell') {
        const result = sellVisitorTrace(store.getSnapshot(), traceId, production, Date.now());
        if (!result.ok) return;
        store.setSnapshot(result.snapshot);
        void renderer.syncTraces(activeIsland(result.snapshot).traces, false);
        refreshProps();
        sound.play('coin', { gain: 0.6 });
        toast.show({
          title: t('traces.sold'),
          text: t('traces.soldText', { value: n(result.coins ?? 0) }),
          icon: 'flower',
        });
      } else {
        movingTraceId = traceId;
        toast.show({ title: t('traces.moveHint') });
      }
    });
  }

  /**
   * Propose de retirer une rue, et dit pourquoi quand c'est impossible.
   *
   * Les deux refus — « elle dessert un bâtiment », « elle relie deux quartiers » — sont
   * des règles de VILLE, pas de graphe : formulés ainsi, le joueur comprend du premier
   * coup ce que la rue tient, au lieu de croire à un bouton cassé. La commande juge
   * elle-même ; on ne pré-juge pas ici, sans quoi les deux copies finiraient par
   * diverger comme l'ont fait celles du placement.
   */
  function askRemoveRoad(col: number, row: number): void {
    const trial = removeRoad(store.getSnapshot(), col, row, Date.now());

    if (!trial.ok) {
      sound.play('refused');
      const serves = trial.refusal === 'serves-building';
      toast.show({
        title: serves ? t('placement.roadServes') : t('placement.roadSplits'),
        text: serves ? t('placement.roadServesHint') : t('placement.roadSplitsHint'),
        icon: 'build',
        domain: 'alert',
      });
      return;
    }

    roadConfirm.ask(t('placement.roadTitle'), () => {
      const result = removeRoad(store.getSnapshot(), col, row, Date.now());
      if (!result.ok) return;
      store.setSnapshot(result.snapshot);
      void renderer.syncRoads(activeIsland(result.snapshot).roads);
      refreshProps();
      sound.play('coin', { gain: 0.4 });
      toast.show({ title: t('placement.roadRemoved') });
    });
  }

  /* ── Boucles ───────────────────────────────────────────────────────── */

  let tickTimer = 0;
  let lastTickAt = performance.now();
  let unsubscribe: (() => void) | null = null;
  let presence: { destroy(): void } | null = null;

  /**
   * Bâtiment d'où part la prochaine pièce, à tour de rôle.
   *
   * Un index tournant plutôt qu'un tirage au sort : le hasard finit toujours par
   * ignorer un bâtiment plusieurs secondes d'affilée, et le joueur croit qu'il ne
   * produit pas. Le tour de rôle fait respirer la ville entière.
   */
  let emitterIndex = 0;
  let lastCoinSoundAt = 0;

  function flyCoins(earned: number, now: number): void {
    const buildings = activeIsland(store.getSnapshot()).buildings;
    if (buildings.length === 0) return;

    emitterIndex = (emitterIndex + 1) % buildings.length;
    const source = buildings[emitterIndex];
    if (source === undefined) return;

    // Le TYPE est transmis : sans lui, la pièce d'un bâtiment 2 × 2 décolle de son coin
    // supérieur au lieu de son centre (audit P1.5).
    const flew = renderer.emitCoin({ col: source.col, row: source.row }, earned, now, source.type);

    /*
      ── LE BÂTIMENT SURSAUTE AVEC SA PIÈCE (ROADMAP 4.4, E§1) ────────────────

      ⚠️ **Seulement si la pièce est réellement partie.** `emitCoin` rend `false` quand le
      plafond de juice a absorbé le gain (E§3) : faire sursauter le bâtiment quand même
      donnerait un mouvement sans cause visible — le pire des deux mondes, puisqu'on
      paierait l'animation en ayant renoncé à ce qu'elle accompagne.

      Une cause visible, un effet visible, au même instant : c'est ce qui apprend au joueur
      quel bâtiment vient de rapporter, sans qu'on ait à le lui dire.
    */
    if (flew) renderer.bumpBuilding(source.id, now);
  }

  /** Décor ambiant sur ce qui n'est ni bâti, ni bitume, ni trace de visiteur. */
  function refreshProps(): void {
    const snapshot = store.getSnapshot();
    const island = activeIsland(snapshot);
    const busy = new Set<string>(island.roads);
    for (const building of island.buildings) {
      for (const cell of footprintCells(building.type, building.col, building.row)) {
        busy.add(`${String(cell.col)}:${String(cell.row)}`);
      }
    }
    // D.1 (0.36.0) — les traces sont des ENTITÉS : le décor d'ambiance leur cède la
    // case, et leur rendu a sa propre passe.
    for (const trace of island.traces) busy.add(`${String(trace.col)}:${String(trace.row)}`);
    /*
      5.3 — LES CASES DÉFRICHÉES ENTRENT DANS `busy`, et c'est tout le mécanisme.

      Le décor d'ambiance ne se supprime pas : il n'existe pas comme donnée, il se dérive
      d'un bruit sur la position. La seule façon de le retirer est donc de déclarer la
      case OCCUPÉE pour la passe de décor — `syncProps` ne pose rien sur ce qui est pris.

      ⚠️ `busy` ne sert QU'À `syncProps`. La construction utilise `occupiedCellsNow()`,
      qui l'ignore : défricher ne libère aucune case, et n'en bloque aucune non plus.
    */
    for (const key of island.cleared) busy.add(key);
    void renderer.syncTraces(island.traces, false);
    // Les routes sont transmises : le décor de bord de rue diffère de celui des champs.
    /*
      Les cases BÂTIES sont transmises à part : le décor d'ambiance décide de sa densité
      selon le voisinage. Une case libre cernée de constructions devient un jardin, une
      case perdue au milieu du terrain reste un pré (5.4).

      ⚠️ On ne peut PAS déduire les cases bâties de `busy` : il porte aussi les routes,
      les traces et les cases défrichées. Confondre les deux mettrait des haies le long
      des rues, là où le mobilier urbain a déjà sa place.
    */
    const built = new Set<string>();
    for (const building of island.buildings) {
      for (const cell of footprintCells(building.type, building.col, building.row)) {
        built.add(`${String(cell.col)}:${String(cell.row)}`);
      }
    }
    void renderer.syncProps(busy, new Set(island.roads), built);
  }

  /**
   * Fait apparaître les traces méritées et pas encore nées, AVEC leur fanfare.
   * Appelé à l'arrivée de nouveaux visiteurs et une fois au démarrage — la migration
   * des villes d'avant 0.36.0, qui ont des visiteurs mais aucune entité.
   */
  function spawnTraces(): void {
    if (!FEATURES.visitorDecorations) return;
    const result = spawnVisitorTraces(store.getSnapshot(), footprintCells, Date.now());
    if (result.born.length === 0) return;
    store.setSnapshot(result.snapshot);
    void renderer.syncTraces(activeIsland(result.snapshot).traces, true);
    sound.play('chime', { gain: 0.6 });
    refreshProps();
  }

  function refreshHud(snapshot: CitySnapshot): void {
    // Une action du joueur ne doit jamais se perdre : on la met en file à chaque
    // changement d'état, l'ordonnanceur décidant seul du moment d'écrire.
    saves.schedule(snapshot, performance.now());
    // Le compteur COMPTE la valeur, il ne saute pas d'un palier à l'autre (E§1.2).
    hud.update(snapshot, totalBoost(snapshot), {
      visitMultiplier: visitBoost,
      visitUntil: visitBoostUntil,
    });
    shop.update(snapshot);
    citySection.update(snapshot);
    /*
      ⚠️ Un panneau qui ne reçoit JAMAIS l'état est un panneau mort (règle 21).
      « Tes visiteurs » — compteur et prochain palier de trace — s'affichait vide :
      sa méthode `update` existait, testée, et n'avait aucun appelant. Même famille
      que `visitorsReceived` lu partout et écrit nulle part.
    */
    socialSection.update(snapshot);
    questPanel.update(snapshot);
    prestigePanel.update(snapshot);
    /*
      Le bandeau lit la MÊME source que le panneau — `prestigePreview` — plutôt que de
      refaire le calcul du seuil. Deux copies d'une règle finissent par diverger, et le
      joueur verrait alors un bandeau proposer ce que l'écran refuse (règle 22).
    */
    const era = prestigePreview(prestigeProgress(snapshot), snapshot.prestigeMultiplier);
    eraNotice.update(era.available, era.nextMultiplier, snapshot.prestigeLevel);
    refreshAdvice(snapshot, Date.now());
    // ROADMAP 3.9 — chaque ère a sa lumière. Inerte tant que le niveau ne change pas.
    renderer.setEra(snapshot.prestigeLevel);
    leagueCard.update(snapshot);
    milestoneBook.update(snapshot);

    /*
      Pastille du dock : nombre de récompenses qui attendent.

      Une quête terminée dans un panneau fermé n'existe pas — le joueur n'ira pas
      inspecter les quatre sections pour vérifier. C'est la pastille qui transforme la
      quête en raison de rouvrir le jeu.
    */
    // Une ère disponible est LA récompense qui attend (demande de Benoit, 18 août) :
    // elle compte dans la pastille, et fait luire l'entrée du dock en violet prestige.
    const eraReady = prestigePreview(
      prestigeProgress(snapshot),
      snapshot.prestigeMultiplier,
    ).available;
    const pending =
      questProgress(snapshot.quests, questCounters(snapshot)).filter(
        (entry) => entry.complete && !entry.claimed,
      ).length +
      weeklyProgress(snapshot.weekly, questCounters(snapshot)).filter(
        (entry) => entry.complete && !entry.claimed,
      ).length +
      (snapshot.quests.streakClaimedDay === snapshot.quests.day ? 0 : 1) +
      (eraReady ? 1 : 0);
    shell.setBadge('progress', pending);
    shell.setGlow('progress', eraReady);

    /*
      ── LA PASTILLE DESCEND JUSQU'À L'ONGLET ──────────────────────────────────

      « Les notifications du menu doivent se répercuter aussi dans les onglets » (Benoit,
      21 août), et c'est la suite logique de l'argument qui a créé la pastille du dock :
      « une récompense qui attend dans un panneau fermé n'existe pas ». Un onglet fermé
      est exactement ça, d'un cran plus bas — le dock disait « il y a quelque chose dans
      Progression » et le joueur ouvrait les trois onglets pour trouver quoi.

      Le compte est RÉPARTI, pas recopié : les quêtes vont sur « Quêtes », l'ère
      disponible sur « Ères ». Une pastille qui affiche le même total sur trois onglets ne
      dit rien de plus que celle du dock.
    */
    const questsPending = pending - (eraReady ? 1 : 0);
    progressTabs.setBadge('quests', questsPending);
    progressTabs.setBadge('eras', eraReady ? 1 : 0);

    /*
      ── Les pastilles ne servaient QU'À la Progression ────────────────────────

      Le mécanisme est générique depuis le début et n'avait qu'un seul appelant :
      les trois autres entrées du dock ne signalaient jamais rien, alors qu'elles
      ont chacune de quoi. Une occasion qu'on ne voit pas n'existe pas — c'est
      l'argument même qui a fait naître la pastille.

      Ce sont des OPPORTUNITÉS, pas des tâches. On ne compte donc pas « combien de
      bâtiments sont abordables » — le compteur serait à dix en permanence et ne
      dirait plus rien : on signale ce qui vient de DEVENIR possible, et une seule
      fois. `seenAffordable` retient ce qui a déjà été signalé ; la pastille
      s'éteint dès que la section est ouverte.
    */
    const counts = buildingCounts(snapshot);
    const unlocked = new Set(unlockedTypes(counts, snapshot.prestigeLevel));
    let freshlyAffordable = 0;
    for (const definition of BUILDINGS) {
      const affordable =
        unlocked.has(definition.type) &&
        snapshot.coins >= nextCost(definition, counts[definition.type] ?? 0);
      if (affordable && !seenAffordable.has(definition.type)) freshlyAffordable++;
      if (affordable) seenAffordable.add(definition.type);
      else seenAffordable.delete(definition.type);
    }
    if (shell.getSection() !== 'build') shell.setBadge('build', freshlyAffordable);

    // Ville : une extension devient payable — c'est le geste qui débloque la place.
    const landReady =
      snapshot.landPurchases < ECONOMY.land.maxPurchases &&
      snapshot.coins >= nextLandCost(snapshot.landPurchases);
    shell.setBadge('city', shell.getSection() === 'city' || !landReady ? 0 : 1);

    // Social : un boost de visite tourne, ou un trophée de visiteur vient d'être
    // gagné. Les deux se voient dans la section, pas depuis la ville.
    shell.setBadge('social', shell.getSection() === 'social' || visitBoost <= 1 ? 0 : 1);

    buildingPanel.update(snapshot);
    store_.update(snapshot);
    // Le tutoriel avance tout seul quand l'action demandée se produit.
    tutorial?.update(snapshot);
  }

  return {
    start() {
      renderer.onPlacementImpact = () => {
        // Le son se déclenche au CONTACT, pas au clic : c'est ce décalage de 300 ms
        // qui fait qu'on entend le bâtiment toucher le sol (E§1.1).
        sound.playPlacement();
      };

      // E§1.2 : les pièces volent jusqu'au compteur, qui pulse à l'arrivée.
      renderer.enableCoinFlight(
        () => {
          const box = hud.coinsEl.getBoundingClientRect();
          const view = canvas.getBoundingClientRect();
          return {
            x: box.left + box.width / 2 - view.left,
            y: box.top + box.height / 2 - view.top,
          };
        },
        () => {
          // Le son est un accent, pas un fond : une pièce sur N seulement le déclenche.
          const at = performance.now();
          if (at - lastCoinSoundAt >= JUICE.coins.soundIntervalMs) {
            lastCoinSoundAt = at;
            sound.play('coin', { gain: JUICE.coins.soundGain }, at);
          }
          hud.coinsEl.classList.remove('is-collected');
          // Forcer un reflow relance l'animation même si deux pièces arrivent coup sur
          // coup : sans ça, la classe rajoutée dans la même frame ne redéclenche rien.
          void hud.coinsEl.offsetWidth;
          hud.coinsEl.classList.add('is-collected');
        },
        // B.1 — le nombre qui gicle, dans la langue et la notation compacte du HUD :
        // c'est le MÊME chiffre que le compteur, vu à sa source.
        (value) => `+${n(value)}`,
      );

      // Compteur de joueurs en ligne. Dégradation totale et silencieuse : sans
      // configuration ou sans réseau, le bloc reste masqué.
      presence = createPresence({
        onChange: (count) => {
          hud.setOnline(count);
        },
      });

      // `pointerdown` d'abord : c'est lui qui ouvre le geste et qui permet de distinguer
      // un tap d'un déplacement de caméra au relâchement.
      canvas.addEventListener('pointerdown', onPointerDown);
      canvas.addEventListener('pointermove', onPointerMove);
      canvas.addEventListener('pointerup', onPointerUp);
      // Un geste interrompu par le système — appel entrant, notification — ne doit pas
      // laisser un tap en suspens qui poserait un bâtiment au relâchement suivant.
      canvas.addEventListener('pointercancel', onPointerCancel);
      document.addEventListener('keydown', onKeyDown);

      /*
        Retour de Stripe (0.38.0). Le `session_id` de l'URL n'est PAS une preuve —
        c'est `stripe-verify`, avec la clé secrète côté serveur, qui dit si la
        session est payée et ce qu'elle vaut. L'URL est nettoyée AVANT la
        vérification : un rechargement pendant l'aller-retour ne rejoue rien, et la
        liste des sessions déjà créditées borne le reste.
      */
      /**
       * Le SEUL endroit où les gemmes payées entrent dans le solde.
       *
       * Trois chemins y mènent — retour de Stripe, rejeu d'une session interrompue,
       * simple démarrage — et ils se recouvrent volontairement : le joueur qui ferme
       * son onglet pendant le paiement passe par les trois. Trois crédits écrits
       * séparément se seraient additionnés ; une réconciliation appelée trois fois ne
       * crédite qu'une, parce qu'elle compare un total à un repère au lieu d'ajouter.
       *
       * Rend ce qui a été effectivement crédité, 0 quand il n'y avait rien à
       * rattraper — l'appelant ne célèbre que sur une valeur.
       */
      /*
        ══ Les bandes d'interface, mesurées et données à la caméra (ROADMAP 1.2) ══

        Le dock sous 1024 px, le panneau latéral au-delà, la barre de ressources en
        haut : autant d'endroits où un bâtiment est affiché mais inatteignable. Sans
        ces mesures, la caméra se borne sur un écran plus grand que celui dont le
        joueur dispose, et un bord de parcelle tombé sous le dock ne remonte jamais.

        ⚠️ Mesuré sur le DOM, jamais codé en dur (règle 14) : le dock change de hauteur
        avec la langue, le panneau de largeur avec le viewport, et la maquette D§7
        prévoit deux mises en page. Une constante serait fausse dans l'une des deux.

        On ne compte que ce qui est réellement OPAQUE et collé à un bord. Un élément
        absent, replié ou de taille nulle ne masque rien : `getBoundingClientRect` rend
        alors 0, et l'inset avec.
      */
      const measureInsets = (): { top: number; right: number; bottom: number; left: number } => {
        const box = (selector: string): DOMRect | null => {
          const el = document.querySelector(selector);
          if (el === null) return null;
          const rect = el.getBoundingClientRect();
          return rect.width > 0 && rect.height > 0 ? rect : null;
        };

        const nav = box('.v-nav');
        const side = box('.v-shell__side');
        const resources = box('#resources');

        // Le dock est en bas SOUS 1024 px et devient un rail vertical à gauche au-delà :
        // on ne suppose pas sa position, on la LIT. Sa largeur trahit son orientation —
        // un rail est plus haut que large, un dock l'inverse.
        const navIsRail = nav !== null && nav.height > nav.width;

        return {
          top: resources === null ? 0 : resources.bottom,
          right: side === null ? 0 : Math.max(0, window.innerWidth - side.left),
          bottom: nav === null || navIsRail ? 0 : Math.max(0, window.innerHeight - nav.top),
          left: nav !== null && navIsRail ? nav.right : 0,
        };
      };

      const refreshCameraInsets = (): void => {
        renderer.setViewportInsets(measureInsets());
      };

      refreshCameraInsets();
      // La bascule de mise en page déplace le dock et fait apparaître le panneau : les
      // insets d'avant décriraient alors un écran qui n'existe plus.
      window.addEventListener('resize', refreshCameraInsets);
      window.matchMedia('(min-width: 1024px)').addEventListener('change', refreshCameraInsets);

      const applyPaidGems = (total: number | null): number => {
        if (total === null) return 0;
        const current = store.getSnapshot();
        const { snapshot, credited } = reconcilePaidGems(current, total);
        if (snapshot === current) return 0;
        store.setSnapshot({ ...snapshot, updatedAt: Date.now() });
        return credited;
      };

      const checkoutParams = new URLSearchParams(location.search);
      const checkout = checkoutParams.get('checkout');
      if (checkout !== null) {
        const sessionId = checkoutParams.get('session_id') ?? '';
        checkoutParams.delete('checkout');
        checkoutParams.delete('session_id');
        const query = checkoutParams.toString();
        history.replaceState(null, '', `${location.pathname}${query === '' ? '' : `?${query}`}`);

        if (checkout === 'success' && sessionId !== '') {
          void verifyCheckout(sessionId).then((result) => {
            if (result === null) return;
            /*
              Le crédit vient du TOTAL du registre, jamais de la valeur de cette
              session : c'est ce qui rend un rechargement de l'URL de retour sans
              effet, sans plus avoir besoin d'une liste locale.

              Un total absent signifie que le registre n'a pas répondu — la session
              reste alors en attente et sera rejouée au prochain démarrage. On
              préfère un crédit différé de quelques secondes à un crédit dont on ne
              saurait plus, demain, s'il a déjà été accordé.
            */
            const credited = applyPaidGems(result.total);
            if (credited <= 0) return;
            hud.celebrateGems();
            sound.play('milestone');
            renderer.celebrateMilestone(performance.now(), true);
            toast.show({
              title: t('store.paid'),
              text: t('store.paidText', { value: n(credited) }),
              icon: 'gem',
              domain: 'gems',
            });
          });
        } else if (checkout === 'cancel') {
          toast.show({ title: t('store.canceled') });
        }
      }

      /*
        FILET DE SÉCURITÉ DU PAIEMENT RÉEL (0.49.0).

        Le bloc ci-dessus ne crédite que si le joueur revient AVEC l'URL de Stripe.
        Onglet fermé après le paiement, réseau coupé pendant la redirection, retour
        par un favori : l'argent était encaissé et les gemmes perdues. On rejoue
        donc, à chaque démarrage, les sessions lancées et jamais réglées — le
        crédit reste idempotent (liste des sessions déjà encaissées).
      */
      void recoverPendingCheckouts()
        .then(
          (total) =>
            // Aucune session à rejouer : on interroge quand même le registre. C'est CE
            // chemin qui répare un stockage local vidé — il n'y reste alors plus aucune
            // session en attente, et l'ancien code ne récupérait donc rien du tout.
            total ?? fetchPaidGemsTotal(),
        )
        .then((total) => {
          const credited = applyPaidGems(total);
          if (credited <= 0) return;
          hud.celebrateGems();
          sound.play('milestone');
          toast.show({
            title: t('store.restored'),
            text: t('store.restoredText', { value: n(credited) }),
            icon: 'gem',
            domain: 'gems',
          });
        });

      // Sonde de test, UNIQUEMENT sous `?debug` (E§3). Sans elle, un parcours
      // automatisé ne peut que regarder des pixels : il ne sait pas distinguer
      // « le sprite n'existe plus » de « le sprite est masqué ». C'est exactement
      // l'angle mort qui a laissé passer la disparition des bâtiments au zoom.
      if (new URLSearchParams(location.search).has('debug')) {
        (globalThis as unknown as { __villopia?: unknown }).__villopia = {
          store,
          renderer,
          shop,
          shell,
          footprintCells,
          footprintOf,
          /*
            La période d'un feu, pour que `probe:traffic` exprime une immobilité en
            CYCLES et non en secondes : « 19 s » ne dit pas si c'est une file d'attente
            ou un blocage, « 1,9 cycle » si (règle 22 — la sonde ne recopie pas une
            constante que la 0.53.0 a déjà changée une fois).
          */
          traffic: { periodMs: LIGHT_PERIOD_MS },
          /*
            Les trois moments d'invitation (6.2). Un partage réel ouvre une feuille
            native ou écrit dans le presse-papiers — deux gestes qu'un navigateur de
            sonde ne sait pas produire. Exposer la porte permet de mesurer ce que
            l'invitation FAIT (s'ouvrir, se refermer, se taire ensuite) au lieu de
            simuler un partage qui n'aboutirait jamais.
          */
          offerAccount,
          /*
            Les réglages, où vit la section Compte — donc la case newsletter (6.3). Le
            bouton qui les ouvre est une icône parmi d'autres dans le HUD : la sonde
            devrait la désigner par sa position, ce que la règle 22 interdit.
          */
          settings,
          /*
            La couche d'écriture du consentement (6.3), exposée pour qu'une sonde puisse
            mesurer l'ALLER-RETOUR RÉEL avec la base — la case, elle, n'écrit qu'une fois
            l'e-mail relié, ce qui demande un lien magique qu'une sonde ne peut pas suivre.
            Sans ça, on ne vérifierait que le dessin de la case et jamais la table.
          */
          newsletter: { record: recordNewsletterConsent, read: fetchNewsletterConsent },
          /*
            Les drapeaux, pour qu'une sonde ne suppose jamais qu'une feature est allumée.
            `consent.probe` en dépend : la case newsletter attend une migration, et sans
            cette lecture la sonde serait rouge sur un choix délibéré (règle 23).
          */
          features: FEATURES,
          /*
            Le panneau de classement, exposé pour que `probe:ranking` puisse forcer ses
            TROIS états vides. Sans cette porte, la sonde ne mesurerait que celui d'une
            ville de sonde — toujours le même — et le seul défaut qui compte (les trois
            états fondus en un) lui échapperait entièrement.
          */
          ranking: rankingPanel,
          /*
            Le moteur de son, pour que `probe:music` puisse allumer la musique sans
            passer par l'écran des réglages — que `probe:settings` défend déjà. Ce qui
            se mesure ici est la CHAÎNE AUDIO, pas l'interface.
          */
          sound,
          /*
            Le catalogue, pour que `make:og` construise sa ville sans DEVINER les noms de
            types. Première rédaction du script : une liste écrite à la main, dont quatre
            entrées sur six n'existaient pas (`bakery`…) — neuf bâtiments posés sur vingt,
            et une image de ville vide. Encore la règle 22 : on nomme ce qu'on vise, et on
            DÉRIVE ce qui se dérive.
          */
          catalogue: BUILDINGS.map((entry) => ({
            type: entry.type,
            unlockAfter: entry.unlockAfter,
          })),
          /*
            La mesure d'audience, exposée pour que `probe:analytics` puisse vérifier
            qu'un événement PART vraiment — et pas seulement que la bibliothèque est
            chargée. C'est la distinction qui a coûté deux passes : trois requêtes vers
            PostHog dont AUCUNE ne portait d'événement.
          */
          analytics: { track, events: EVENTS },
          /*
            La boutique de gemmes, qui est une MODALE : elle ne s'ouvre pas en changeant
            de section. `tabs.probe` en a besoin pour relever la quatrième barre
            d'onglets du jeu sans deviner quel compteur du HUD la déclenche.
          */
          storePanel: store_,
          /*
            Le panneau de bâtiment s'ouvre par un tap SUR LA VILLE, à une position que le
            harnais devrait recalculer depuis (col, row), la caméra et l'orientation de
            vue. Il le ferait avec la même arithmétique que le moteur — donc sans jamais
            pouvoir la contredire. L'exposer permet au parcours de tester ce que le
            panneau FAIT (la vente confirmée) plutôt que de re-tester la projection.
          */
          buildingPanel,
          /*
            Le panneau d'une TRACE, exposé pour la même raison que celui d'un bâtiment :
            il s'ouvre par un tap sur la ville, à une position que la sonde devrait
            recalculer depuis (col, row), la caméra et le zoom — avec la même
            arithmétique que le moteur, donc sans jamais pouvoir le contredire.
            L'exposer permet de mesurer ce que le panneau MONTRE (le montant de la
            revente, 2.9d) plutôt que de re-tester la projection.
          */
          openTracePanel,
          /*
            Les commandes pures, exposées pour les sondes.

            Sans elles, `probe:roads` devait importer `/src/core/gameState.ts` — un chemin
            que seul le serveur de développement sert. La sonde ne pouvait donc PAS
            mesurer la production, ce qui est exactement ce que la règle 11 exige d'elle :
            « un livrable que je n'ai pas mesuré en ligne n'est pas vérifié ».

            Ce sont des fonctions pures qui ne modifient rien : elles rendent un nouveau
            snapshot, à l'appelant de l'appliquer. Et tout ceci vit sous `?debug`, absent
            de l'expérience du joueur.
          */
          /*
            Le catalogue des repères (2.3), lu depuis le JEU par la sonde plutôt que
            recopié dans son fichier : une liste dupliquée se désynchronise au premier
            prix retouché, et la sonde validerait alors un débit qui n'est plus le bon
            (règle 22).
          */
          landmarks: LANDMARKS,
          commands: {
            placeBuilding,
            purchaseLand,
            moveBuilding,
            removeRoad,
            activeIsland,
            buildingCounts,
            unlockedTypes,
            /*
              Pas une commande pure : le rafraîchissement du décor, exposé pour que la
              sonde des traces (D.1) puisse rejouer ce que `refreshVisits` déclenche
              quand de vrais visiteurs arrivent — sans attendre le sondage réseau de
              deux minutes qu'aucune borne de sonde ne peut se permettre.
            */
            refreshProps,
            // Traces (0.36.0) : l'apparition et la vente, pour la sonde des traces.
            spawnTraces,
            sellVisitorTrace,
            traceSellValue,
            /*
              Décorations (2.9) : l'achat, la pose et le barème de revente. Exposés
              pour que `decor.probe.mjs` prépare son cas par le MÊME chemin que le
              jeu — une sonde qui fabriquerait ses entités à la main mesurerait un
              état que le joueur ne peut pas atteindre.
            */
            buyDecoration,
            placeDecoration,
            boughtDecorationSellValue,
            currentProduction,
            /*
              Scelle un snapshot au format de stockage v2 (A.2). La sonde de la pluie
              du retour (B.6) doit PLANTER une sauvegarde datée d'il y a trois heures :
              sans le sceau, la relecture la rejetterait comme corrompue — ce qui est
              exactement le rôle du sceau, et la raison pour laquelle il s'expose ici
              plutôt que d'être recopié dans la sonde (règle 22).
            */
            sealSnapshot: (snapshot: CitySnapshot) => sealEnvelope(serialize(snapshot)),
          },
          /*
            ⚠️ **L'équilibrage, exposé — pour que les sondes cessent de le recopier.**

            `horizon.probe.mjs` amenait le joueur au seuil de prestige en injectant
            `totalProduced: 1e12`, une valeur juste au moment où elle a été écrite. Le
            recalibrage du prestige l'a rendue insuffisante d'un facteur dix mille, et la
            sonde a échoué sur un bouton resté grisé — un symptôme qui ne disait rien de
            la cause.

            C'est exactement la règle 15 : **une valeur dérivée d'une constante se
            calcule depuis cette constante, elle ne se recopie pas.** Une sonde qui lit
            l'équilibrage réel du bundle ne peut plus se désynchroniser de lui.
          */
          // GEM_SPENDING ajouté avec le lot 1 : la sonde du puits à gemmes DÉRIVE les
          // prix qu'elle vérifie au lieu de les recopier (règle 22).
          // DECORATIONS ajouté avec 2.9d : la sonde DÉRIVE le montant de revente
          // qu'elle attend (prix d'achat × barème) au lieu de le recopier.
          balance: { PRESTIGE, ECONOMY, GEM_SPENDING, DECORATIONS, CLEARING },
          /*
            Le FORMATEUR de nombres, exposé pour la même raison que l'équilibrage.

            Une sonde qui lit un montant à l'écran lit du texte FORMATÉ : `n(1500)`
            rend « 1,5 k ». Celle de la revente comparait le résultat d'un
            `parseInt` sur les chiffres du libellé — donc 15 — à la valeur attendue,
            1 500, et rougissait sur un défaut qui n'existait pas. Elle formate
            désormais son attendu par la MÊME fonction que l'écran : les deux ne
            peuvent plus diverger (règle 22).
          */
          format: { n },
          /*
            La chaîne de paiement est-elle CONFIGURÉE ?

            `startCheckout` sort sur `isSupabaseConfigured()` : sans `.env`, la fonction
            n'est jamais appelée et les deux contrôles Stripe du parcours échouent quel
            que soit l'état du jeu. Or la CI construit sans secrets — ils y étaient donc
            rouges en permanence, ce que le projet refuse (voir l'en-tête de
            `scripts/probes.mjs`). La sonde LIT donc la configuration au lieu de la
            supposer, et annonce qu'elle ne joue pas ces lignes plutôt que d'échouer.
          */
          payments: { configured: isSupabaseConfigured() },
          /*
            Le registre des gemmes payées (ROADMAP 0.1), exposé pour sa sonde.

            On expose les FONCTIONS RÉELLES, pas des valeurs : `paidGems.probe.mjs` doit
            pouvoir demander l'identité du joueur et le total du registre par le même
            chemin que le jeu. Une sonde qui refait un `signInAnonymously` de son côté
            ouvrirait un second compte et mesurerait un joueur qui n'est pas celui de
            l'écran — le contraire de ce qu'elle prétend vérifier (règle 22).
          */
          paidGems: { ownerId: ensureAuthSession, serverTotal: fetchPaidGemsTotal },
          /*
            La synchronisation distante (ROADMAP 0.2), exposée pour sa sonde.

            `sync.city()` rend la ville distante telle que le JEU la connaît. Refaire
            la requête depuis la sonde ne prouverait rien : elle passerait par un
            chemin que le joueur n'emprunte pas, et resterait verte le jour où
            `ensureCity` cesse d'aboutir.
          */
          sync,
          /*
            La durée du cycle jour/nuit, exposée pour la même raison que l'équilibrage :
            `night.probe.mjs` l'avait RECOPIÉE — huit minutes au lieu de six — et visait
            donc de mauvaises phases. Elle mesurait consciencieusement l'aube en croyant
            mesurer le plein jour, et concluait que la nuit était plus claire que le
            jour. Une sonde qui recopie une constante ne mesure que sa propre
            arithmétique (règle 22).
          */
          dayLengthMs: DAY_LENGTH_MS,
          /*
            Aucun service worker n'est enregistré en développement
            (`devOptions.enabled: false`) : sans cette sonde, l'avis de nouvelle version ne
            serait vérifiable QUE sur la production, donc jamais avant de la livrer. La
            règle 18 demande de voir un écran après une séquence de gestes, pas de l'espérer.
          */
          updateNotice,
          companion,
        };
      }

      /*
        Bascule de journée au démarrage : c'est le seul moment où l'on sait que le
        joueur revient. La faire à chaque tick coûterait un calcul de date dix fois par
        seconde pour un résultat identique.
      */
      const opening = store.getSnapshot();
      const today = dayNumber(Date.now());
      const thisWeek = weekNumber(today);

      // Les deux périodes basculent au même endroit mais indépendamment : une semaine
      // peut tourner sans que le jour ait changé de valeur pour les quotidiennes.
      if (opening.quests.day !== today || opening.weekly.week !== thisWeek) {
        const counters = questCounters(opening);
        store.setSnapshot({
          ...opening,
          quests: rollDay(opening.quests, today, counters),
          weekly: rollWeek(opening.weekly, thisWeek, counters),
        });
      }

      /*
        Gains hors-ligne au CHARGEMENT — l'étape 8 proprement dite.

        Le tick couvre l'onglet resté ouvert en arrière-plan ; celui-ci couvre le jeu
        fermé, qui est le cas courant d'un idle. La durée vient de `updatedAt` du
        snapshot relu, jamais d'une horloge écrite dans la sauvegarde — une save
        éditée à la main pourrait sinon forger une absence de dix ans.

        Une absence NÉGATIVE (horloge reculée) est ignorée : `offlineEarnings` la borne
        déjà à zéro, mais l'écrire ici rend l'intention lisible.
      */
      const awayMs = Math.max(0, Date.now() - loaded.snapshot.updatedAt);
      if (awayMs > 0 && loaded.kind === 'loaded') {
        const opening = store.getSnapshot();
        /*
          `creditReturn` porte la règle ET la nuit blanche : elle décide du plafond, du
          rendement, et consomme la réserve seulement si elle change quelque chose.
        */
        const credited = creditReturn(opening, awayMs, Date.now());
        const away = credited.earnings;
        if (away.coins > 0) {
          store.setSnapshot(credited.snapshot);
          // Le journal ne s'ouvre qu'au-delà de dix minutes : une modale à chaque
          // rechargement serait une taxe, pas une célébration.
          const shown = returnScreen.show({
            coins: away.coins,
            creditedMs: away.creditedMs,
            cappedMs: away.cappedMs,
            // Le rendement RÉELLEMENT appliqué, pas la constante de catalogue : avec une
            // nuit blanche consommée, l'écran aurait annoncé 25 % en créditant 50 %.
            efficiency: away.efficiency,
            nightUsed: credited.nightUsed,
            /*
              L'offre de rachat n'apparaît que si elle a un objet : des heures écrêtées,
              et pas de nuit blanche déjà dépensée pour ce retour-ci. La proposer après
              coup sur un retour déjà généreux serait vendre deux fois la même chose.
            */
            ...(away.cappedMs > 0 && !credited.nightUsed
              ? { recoverCost: GEM_SPENDING.night.cost }
              : {}),
          });
          // C'est l'écran qui est ouvert qui fixe l'objet du rachat (voir `pendingAwayMs`).
          if (shown && away.cappedMs > 0 && !credited.nightUsed) pendingAwayMs = awayMs;
          // La pluie n'est armée QUE si l'écran s'est montré : un retour de trois
          // minutes ne mérite ni écran ni pluie.
          if (shown) pendingRain = away.coins;
        }
      }

      unsubscribe = store.subscribe(refreshHud);
      refreshHud(store.getSnapshot());

      // La grille vient de l'ÉTAT, pas d'une constante : une ville sauvegardée avec
      // des extensions doit retrouver son terrain, et la migration des emprises peut
      // elle aussi avoir élargi la grille pour ne perdre aucun bâtiment.
      renderer.setGridTo(activeIsland(store.getSnapshot()).grid);

      // La ville rechargée doit apparaître, sans le juice de pose : `animateNew: false`.
      // Rejouer dix chutes de bâtiments à chaque ouverture serait une célébration de
      // quelque chose que le joueur a fait hier — E§1.1 célèbre l'action, pas l'état.
      void renderer.syncBuildings(activeIsland(store.getSnapshot()).buildings, false);
      void renderer.syncRoads(activeIsland(store.getSnapshot()).roads);
      refreshProps();
      // Migration 0.36.0 : une ville d'avant les entités a des visiteurs et aucune
      // trace — elles poussent ici, une fois, avec leur fanfare (elles sont neuves).
      spawnTraces();
      /*
        Réparation des villes ABÎMÉES avant le correctif du 20 août : tant que
        `canPlace` ignorait les traces, une maison pouvait se poser sur une décoration
        et la rendre injoignable — donc invendable, et perdue à l'ère suivante si elle
        avait été payée. Ici, une fois, au chargement : ce que le joueur a payé
        réapparaît sur une case libre ou dans son sac.
      */
      store.setSnapshot(rescueBuried(store.getSnapshot()));

      lastTickAt = performance.now();
      // `window` et non `globalThis` : avec les types Node chargés, le second rend un
      // `Timeout` et non un identifiant numérique.
      tickTimer = window.setInterval(() => {
        const now = performance.now();
        const elapsed = now - lastTickAt;
        lastTickAt = now;

        /*
          ── LA MUSIQUE SUIT LE CIEL (12.9) ────────────────────────────────

          On RELAIE la phase de `skyAt()`, on ne la recalcule pas. Deux horloges de cycle
          dans un même jeu finissent par se croiser, et la musique passerait à l'ambiance
          de nuit pendant que le ciel est encore bleu — un décalage qu'on sent sans savoir
          le nommer.

          Coût nul quand la musique est coupée : `setMusicPhase` écrit un nombre dans un
          objet qui n'existe même pas tant qu'aucun geste n'a créé l'`AudioContext`.
        */
        sound.setMusicPhase(skyAt(Date.now()).phase);

        /*
          ── LA RELANCE DE NOM (22 août) ───────────────────────────────────

          « Il faut forcer à renommer sa ville […] plusieurs fois tant que ce n'est pas
          fait. » Elle ne part QUE si la ville distante existe : sans slug, il n'y a rien
          à nommer, et relancer un joueur qui n'a pas encore posé de bâtiment serait lui
          demander quelque chose qu'il ne peut pas faire.

          La cadence est de six heures et vit dans `localStorage` — pas dans le snapshot :
          c'est une préférence d'affichage locale, pas un fait de partie, et elle n'a rien
          à faire dans une sauvegarde synchronisée entre appareils.

          ⚠️ Le tap emmène le joueur DANS la section Ville, là où le champ se trouve. Un
          rappel qui dit quoi faire sans y conduire fait porter au joueur la recherche du
          bouton — et c'est ainsi qu'un rappel devient une nuisance.
        */
        const city = sync.city();
        if (city !== null) {
          const lastRaw = localStorage.getItem(RENAME_NUDGE_KEY);
          const last = lastRaw === null ? null : Number.parseInt(lastRaw, 10);
          const at = Date.now();
          if (shouldNudgeRename(city.name, defaultCityNames(), last, at)) {
            localStorage.setItem(RENAME_NUDGE_KEY, String(at));
            toast.show({
              title: t('city.renameNudge'),
              text: t('city.renameNudgeText', { value: n(RENAME_REWARD_GEMS) }),
              icon: 'city',
              domain: 'gems',
            });
            /*
              ⚠️ Le toast NE PEUT PAS être cliquable : la couche est en
              `pointer-events: none` par décision (D§3) — « un toast qui intercepte un tap
              pendant que le joueur construit est une frustration silencieuse ». On pointe
              donc l'entrée du dock, comme le fait le tutoriel : le message dit quoi faire,
              la pulsation dit où.
            */
            shell.pulse('city');
          }
        }

        /*
          Le temps écoulé se RÉPARTIT entre présence et absence (audit P2.1).

          Il était crédité en entier, plein tarif : un onglet laissé en arrière-plan
          une nuit rendait huit heures de production pleine, là où la conception
          accorde huit heures à 25 %. Fermer le jeu était donc puni par rapport à le
          laisser ouvert — l'inverse exact de l'intention. Au passage, ce branchement
          livre l'étape 8 : `offlineEarnings` était écrit et testé sans aucun appelant.
        */
        const split = splitElapsed(elapsed);
        let snapshot = store.getSnapshot();

        if (split.awayMs > 0) {
          // Onglet resté en arrière-plan : on crédite comme une absence, sans écran —
          // le joueur n'a pas « rouvert » le jeu, il y revient d'un coup d'œil.
          /*
            Même règle qu'au chargement, même fonction. Un onglet laissé ouvert une nuit
            EST une absence : la nuit blanche s'y applique donc aussi, et pour la même
            raison — le joueur a payé pour du temps, pas pour un geste d'ouverture.
          */
          const credited = creditReturn(snapshot, split.awayMs, Date.now());
          const away = credited.earnings;
          if (away.coins > 0) {
            snapshot = credited.snapshot;
            /*
              Un HOQUET n'est pas une absence, et ne s'annonce pas.

              `splitElapsed` traite tout écart au-delà de 200 ms comme du temps « à
              distance » — et il a raison de le faire, un onglet étranglé ne doit pas
              produire à plein tarif. Mais 200 ms sont aussi ce que coûtent une passe de
              chargement de textures ou dix poses d'un coup. Le toast se déclenchait donc
              sur un ralentissement ordinaire, et affichait **« Content de te revoir ! +0,0
              pièces produites pendant ton absence (0 s) »** — mesuré pendant l'ajout de
              l'achat en lot.

              Une célébration qui se déclenche pour rien apprend à ignorer les
              célébrations. Le crédit reste, lui, exact à la milliseconde : seule
              l'annonce a un seuil.
            */
            if (split.awayMs >= ABSENCE_WORTH_TELLING_MS) {
              toast.show({
                title: t('offline.welcomeBack'),
                text: t('offline.earned', {
                  value: n(away.coins),
                  duration: duration(away.creditedMs),
                }),
              });
            }
          }
        }

        /*
          Bascule de journée DANS le tick (audit P2.2).

          Elle n'avait lieu qu'au démarrage : un idle se laisse ouvert, donc à minuit
          rien ne tournait, et au rechargement deux jours plus tard la chaîne de
          connexion repartait de 1 alors que le joueur avait « joué » chaque jour. La
          comparaison est celle de deux entiers, dix fois par seconde — le coût
          invoqué dans l'ancien commentaire était illusoire.
        */
        const today = dayNumber(Date.now());
        const thisWeek = weekNumber(today);
        if (snapshot.quests.day !== today || snapshot.weekly.week !== thisWeek) {
          const counters = questCounters(snapshot);
          snapshot = {
            ...snapshot,
            quests: rollDay(snapshot.quests, today, counters),
            weekly: rollWeek(snapshot.weekly, thisWeek, counters),
          };
        }

        /*
          L'accélération achetée en gemmes s'applique ICI, au crédit de production.

          Elle est DÉRIVÉE du temps (`boostMultiplier`) plutôt que pilotée par une minuterie :
          un onglet masqué une heure retrouve le bon état en revenant, au lieu d'avoir à
          rattraper une expiration manquée. Même principe que les feux de circulation et
          que les animations — le temps est fourni, jamais lu.
        */
        const result = tick(snapshot, split.liveMs, Date.now(), totalBoost(snapshot));
        saves.schedule(result.snapshot, now);

        if (result.earned > 0) {
          // La pièce part AVANT que l'état ne change : elle doit décoller du bâtiment
          // qui vient de produire, pas d'une ville déjà mise à jour.
          flyCoins(result.earned, now);
        }
        // Les jalons de production s'atteignent ICI, en produisant — pas seulement en
        // posant. `claimMilestones` est pur et le catalogue court : 10 Hz est indolore,
        // et la barre du HUD ne reste jamais pleine.
        if (result.snapshot !== snapshot || snapshot !== store.getSnapshot()) {
          applyWithMilestones(result.snapshot);
        }
      }, 1000 / ECONOMY.tickHz);
    },

    stop() {
      globalThis.clearInterval(tickTimer);
      globalThis.clearInterval(boostTimer);
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerup', onPointerUp);
      canvas.removeEventListener('pointercancel', onPointerCancel);
      document.removeEventListener('keydown', onKeyDown);
      unsubscribe?.();
      // Une pose faite juste avant l'arrêt doit survivre : on écrit sans attendre le
      // prochain intervalle.
      saves.flush();
      saves.dispose();
      sync.flush();
      sync.destroy();
      presence?.destroy();
      // La surveillance porte une minuterie d'une demi-heure : sans cet arrêt, elle
      // survivrait au jeu détruit et rappellerait `update()` sur un enregistrement dont
      // plus personne n'écoute la réponse.
      stopWatchingUpdates();
      updateNotice.destroy();
      installBanner.destroy();
      hud.destroy();
      account.destroy();
      versionInfo.destroy();
      returnScreen.destroy();
      preferences.destroy();
      settings.destroy();
      buildingPanel.destroy();
      sellConfirm.destroy();
      roadConfirm.destroy();
      clearConfirm.destroy();
      prestigeConfirm.destroy();
      prestigePanel.destroy();
      // L'invitation vit sur `body`, hors de la coquille : sans ce retrait elle survivrait
      // au démantèlement et s'ouvrirait sur un jeu qui n'existe plus.
      invite.destroy();
      store_.destroy();
      shop.destroy();
      shell.destroy();
    },

    getSnapshot: () => store.getSnapshot(),
  };
}
